import * as Cesium from 'cesium'
import {
  Cartesian3,
  Color,
} from 'cesium'
import { state, viewer } from './use-cesium-map'
import { FrustumOptions, FrustumTool } from './draw-frustum'
import { nextTick } from 'vue'
import {
  currentSettingData,
  getEllipsoidCartesian,
  getHeight,
  getHeightPoint,
  getPointPosition,
  getSetFlyToFirstPoint,
  isMove,
  isMoveAlt,
  isSave,
  moveStyle,
  setHpr,
} from '/@/utils/kmlParse'
import { FollowBadType, ROUTE_TYPE, WaylineMapType } from '/@/types/wayline'
import emitter from '/@/event-bus'
import { PolylineDirectionMaterial } from './polyline-direction-material'
import { message } from 'ant-design-vue'
import { currentStripRouteSettingData } from '/@/utils/kmlStrip'
import { cloneDeep } from 'lodash'

// 常量定义和样式配置
const IDEAL_BASE_PITCH = -90 // 理想俯仰角
const BASE_FOVBYMAP = 60 // 1X时的基准FOV（度）
const BASE_PITCH = 0 // 基础俯仰角
export const BASE_FOV = 2 // 计算BASE_FOVBYMAP初始值
const BASE_HEADING = 0.0 // 基础偏航角
const BASE_ROLL = 0.0
const BASE_FAR = 200 // 远截面的距离  视野终点
const BASE_NEAR = 5 // 近截面的距离 视野起点
const MAX_ZOOM = 200 // 最大变焦倍数
// 在文件顶部的常量定义区域添加或修改
const FRUSTUM_CONFIG = {
  // 视锥体基础参数
  BASE_FOV: 60, // 基础视场角（度）
  BASE_NEAR: 5, // 近截面距离
  BASE_FAR: 200, // 远截面距离
  BASE_HEADING: 0, // 基础航向角
  BASE_PITCH: 0, // 基础俯仰角（调整为向下视角）
  BASE_ROLL: 0, // 基础翻滚角
  ASPECT_RATIO: 0.75, // 宽高比（16:9）
  COLOR: '#4cc9f0', // 视锥体颜色
  OPACITY: 0.3, // 透明度
  SHOW_MODEL: false, // 是否显示飞机模型
  SHOW_FRUSTUM: true, // 是否显示视锥体
}

// 多点位连线渲染
const polylineOption = {
  width: 3,
  material: new Cesium.PolylineOutlineMaterialProperty({
    color: Cesium.Color.BLUE,
    outlineColor: Cesium.Color.WHITE,
    outlineWidth: 3,
  }),
}

export interface PointAttributes {
  color: string;
  text?: string;
  imgUrl?: string;
}

export interface WaypointInfo {
  longitude: number;
  latitude: number;
  height: number;
  id: string;
  isStartingPoint?: boolean;
  actions?: any[];
}

export interface RouteEntity {
  waypointSpeed: any;
  useGlobalSpeedParam: any;
  waypointHeadingParam: any;
  useGlobalHeadingParam: any;
  waypointTurnDampingDist: any;
  waypointTurnMode: any;
  useGlobalTurnParam: any;
  isUseGlobalHeight: any;
  index?: number;
  hpr: {
    heading: number;
    pitch: number;
    roll: number;
  };
  id: string;
  type: string;
  entityId: string;
  entity: Cesium.Entity; // 这个保持在类内部使用，不暴露给外部
  point: Cesium.Entity;
  positions: Cesium.Cartesian3[]; // 这个也需要转换
  position: Cesium.Cartesian3; // 当前位置
  waypoints: WaypointInfo[];
  eventList: any[];
}
// 创建用于存储的接口
export interface SerializableRouteEntity {
  id: string;
  type: string;
  positions: Array<{
    longitude: number;
    latitude: number;
    height: number;
  }>;
  waypoints: WaypointInfo[];
  eventList: any[];
}
// 在接口定义区域添加
export interface PolygonRouteEntity extends SerializableRouteEntity {
  type: 'PolygonRoute';
  vertices: Array<{
    longitude: number;
    latitude: number;
    height: number;
  }>;
  isClosed: boolean;
  area?: number; // 多边形面积（可选）
}
// 定义点实体扩展接口
interface PointEntity extends Cesium.Entity {
  pointIndex?: number;
  typeInfo?: string;
  parent: Cesium.Entity;
}
// 定义多边形实体扩展接口
interface PolygonEntity extends Cesium.Entity {
  attrColor?: string;
  typeInfo?: string;
  latlngList?: { longitude: number; latitude: number; height: number }[];
  attr?: {
    horizontalDis?: number;
    horizontalArea?: number;
    heightDiff?: number;
  };
  isFromAPI?: boolean;
}

interface PointLineEntity extends Cesium.Entity {
  attrColor?: string;
  typeInfo?: string;
  latlngList?: { longitude: number; latitude: number; height: number }[];
  attr?: {
    horizontalDis?: number;
    horizontalArea?: number;
    heightDiff?: number;
  };
  isFromAPI?: boolean;
}

// 航线航点工具类
export default class RouteTool {
  private viewer: Cesium.Viewer;
  private store: any;
  public _dataSource: Cesium.CustomDataSource;
  private _drawHandler: Cesium.ScreenSpaceEventHandler | null = null;
  private _drawPointLine: RouteEntity[] = [];
  // 在类内部添加frustumTool的引用
  public _frustumTool: FrustumTool | null = null;
  public heightMode: string = '';
  // 存储所有视锥体的映射
  private _frustumMap: Map<string, FrustumTool> = new Map();
  private _hasStartingPoint: boolean = false;
  private _startingPoint: Cesium.Cartesian3 | null = null;
  private _batchAdding: boolean = false;
  private _editPointIndex: number | null = null;
  // 航线航点数据
  private waypoints: WaypointInfo[] = [];
  private routes: RouteEntity[] = [];
  private _selectedRoute: RouteEntity | null = null;

  // 样式配置
  private _styles = {
    waypoint: {
      color: Cesium.Color.RED,
      pixelSize: 10,
      outlineColor: Cesium.Color.YELLOW,
      outlineWidth: 2,
      clampToGround: false,
    },
    startingPoint: {
      color: Cesium.Color.GREEN,
      pixelSize: 15,
      outlineColor: Cesium.Color.WHITE,
      outlineWidth: 2,
      clampToGround: false,
    },
    route: {
      width: 3,
      material: new PolylineDirectionMaterial(),
      clampToGround: false,
    },
    selected: {
      waypoint: {
        color: Cesium.Color.BLUE,
        pixelSize: 15,
        outlineColor: Cesium.Color.WHITE,
        outlineWidth: 3,
        clampToGround: false,
      },
      route: {
        width: 5,
        clampToGround: false,
        material: new PolylineDirectionMaterial(),
      },
    },
  };

  private _polygonPoints: Cesium.Cartesian3[] = [];
  private _polygonRoutes: PolygonRouteEntity[] = [];
  private _isDrawingPolygon: boolean = false;
  private _maxPolygonPoints: number = 4; // 最多4个点
  private _polygonDrawingHandler: Cesium.ScreenSpaceEventHandler | null = null;
  private _selectHandler: Cesium.ScreenSpaceEventHandler | null = null;
  private _isDragging: boolean = false;
  private _draggingEntity: Cesium.Entity | null = null;
  private _draggingIsVirtual: boolean = false;
  private _dragStartPosition: Cesium.Cartesian3 | null = null;
  private _selectedDrawing: any = null;
  private _originalPositions: Cesium.Cartesian3[] = [];
  public _originalPositionsList: RouteEntity[] = [];
  private _virtualPointIndex: number = -1;
  private _draggablePoints: Cesium.Entity[] = [];
  private _virtualPoints: Cesium.Entity[] = [];
  private _isProcessingClick: boolean = false;
  private _isSelectionEnabled: boolean = false;
  private _isDrawing: boolean = false;
  private _editRouter = null;
  // 带状航线相关
  private _isDrawingStripCenterLine: boolean = false;
  private _stripCenterPoints: Cesium.Cartesian3[] = [];
  // 在类中添加带状虚拟点存储（可放在 stripPointComObj 中或单独 Map）
  private _stripVirtualPointsMap: Map<any, Cesium.Entity[]> = new Map(); // 每个 route 关联一组虚拟点
  private _stripRoutes: any[] = [];
  private _stripDrawingHandler: Cesium.ScreenSpaceEventHandler | null = null;
  private _stripEditHandler: Cesium.ScreenSpaceEventHandler | null = null;
  private stripPointComObj: {
    positionList: Cesium.Cartesian3[];
    copyPositionList: Cesium.Cartesian3[];
    movePositions: Cesium.Cartesian3[];
    polygonEntity: PolygonEntity | null;
    pointTextEntity1: Cesium.Entity | null;
    pointTextEntity2: Cesium.Entity | null;
    pointEntityList: PointEntity[];
    textEntityList: Cesium.Entity[];
    editPointEntityList: PointEntity[];
    polylineEntity: PointLineEntity | null;
    entityList: any[];
  } = {
    positionList: [],
    copyPositionList: [],
    movePositions: [],
    polygonEntity: null,
    pointTextEntity1: null,
    pointTextEntity2: null,
    pointEntityList: [],
    textEntityList: [],
    editPointEntityList: [],
    entityList: [],
  } as any;

  private polygonComObj: {
    positionList: Cesium.Cartesian3[];
    copyPositionList: Cesium.Cartesian3[];
    movePositions: Cesium.Cartesian3[];
    polygonEntity: PolygonEntity | null;
    pointTextEntity1: Cesium.Entity | null;
    pointTextEntity2: Cesium.Entity | null;
    pointEntityList: PointEntity[];
    textEntityList: Cesium.Entity[];
    editPointEntityList: PointEntity[];
    polylineEntity: Cesium.Entity | null;
    entityList: any[];
  } = {
    positionList: [],
    copyPositionList: [],
    movePositions: [],
    polygonEntity: null,
    pointTextEntity1: null,
    pointTextEntity2: null,
    pointEntityList: [],
    textEntityList: [],
    editPointEntityList: [],
    entityList: [],
  } as any;

  /**
   * 构造函数
   * @param viewer Cesium Viewer实例
   * @param store Vuex store实例（可选）
   */
  constructor (viewer: Cesium.Viewer, store: any = null) {
    this.viewer = viewer
    this.store = store
    this._dataSource = new Cesium.CustomDataSource('routeDataSource')
    this.viewer.dataSources.add(this._dataSource)
    this._polygonPoints = []
    this._polygonRoutes = []
    this._isDrawingPolygon = false
  }

  /**
   * 激活航线绘制模式
   */
  activate (): void {
    this.clearSelection()
    this.clearCurrentDrawing()
    this._registerEvents()
  }

  /**
   * 激活面状航线绘制模式
   */
  activatePolygonDrawing (): void {
    this.clearSelection()
    this.clearCurrentDrawing()
    this.clearPolygonDrawing()
    this._isDrawingPolygon = true
    this._registerPolygonEvents()
  }

  /**
   * 停止绘制
   */
  stopDrawing (): void {
    this.clearCurrentDrawing()
    this._isSelectionEnabled = true
    this._isDrawing = false
  }

  private _getPickPosition (screenPosition: any): Cesium.Cartesian3 | null {
    // 方法1: 使用 scene.pickPosition（优先）
    let position = this.viewer.scene.pickPosition(screenPosition)
    if (position && this._isValidPosition(position)) {
      return position
    }

    // 方法2: 使用椭球体拾取
    const ellipsoid = this.viewer.scene.globe.ellipsoid
    position = this.viewer.camera.pickEllipsoid(screenPosition, ellipsoid)
    if (position && this._isValidPosition(position)) {
      return position
    }

    // 方法3: 使用 globe.pick（备用）
    const ray = this.viewer.camera.getPickRay(screenPosition)
    if (ray) {
      position = this.viewer.scene.globe.pick(ray, this.viewer.scene)
      if (position && this._isValidPosition(position)) {
        return position
      }
    }

    return null
  }

  private _getPickObject (position: any, viewer: Cesium.Viewer) {
    let event, pick
    const ray = new Cesium.Ray()
    const scene = viewer.scene
    let i
    if (scene.mode !== Cesium.SceneMode.MORPHING) {
      if (scene.pickPositionSupported) {
        event = viewer.scene.pickPosition(position, i)
      }
      pick = scene.pick(position)
      if (Cesium.defined(pick)) {
        const pickRay = scene.camera.getPickRay(position, ray)
        pickRay && (event = scene.globe.pick(pickRay, scene, i))
      } else if (
        viewer.terrainProvider instanceof Cesium.EllipsoidTerrainProvider
      ) {
        event = viewer.camera.pickEllipsoid(position, scene.globe.ellipsoid, i)
      } else {
        const pickRay = scene.camera.getPickRay(position, ray)
        pickRay && (event = scene.globe.pick(pickRay, scene, i))
      }
    }
    if (Cesium.defined(event) || Cesium.defined(pick)) {
      return {
        pickedObject: pick,
        position: event,
        screenPosition: position,
      }
    }
  }

  /**
   * 注册起始点点击事件
   */
  registerStartPointEvent (): void {
    this._removeDrawEvents()

    // 清除现有起始点
    const startingPoints = this.routes.filter(
      (el) => el.type === 'StartingPoint'
    )
    startingPoints.forEach((el) => {
      this.deleteRoute(el.id)
      this._dataSource?.entities.remove(el.entity)
      this._dataSource.entities.remove(el.point)
    })
    this.routes = this.routes.filter((el) => el.type !== 'StartingPoint')
    // 重置起始点状态
    this._hasStartingPoint = false
    this._startingPoint = null

    this._drawHandler = new Cesium.ScreenSpaceEventHandler(
      this.viewer.scene.canvas
    )
    this._drawHandler.setInputAction(async (e: any) => {
      const pickObject = this._getPickObject(e.position, this.viewer)
      const positions = pickObject?.position

      if (!positions) return
      // 添加起始点
      await this.addStartingPoint(positions)
      this._removeDrawEvents()
      isSave.value = true
      if (this.store) {
        this.store.commit('SET_START_POINT_FLAG', false)
        emitter.emit('endRouteStartPoint')
      }
    }, Cesium.ScreenSpaceEventType.LEFT_CLICK)
  }

  /**
   * 注册鼠标事件
   * @private
   */
  private _registerEvents (): void {
    this._removeDrawEvents()
    this._drawHandler = new Cesium.ScreenSpaceEventHandler(
      this.viewer.scene.canvas
    )
    this._leftClickEventForPointLine()
  }

  /** 设置默认航线点 */
  public async PointEventForAddPointLine (positions_latlng: { lon: number, lat: number, height: number }): void {
    const cartesian3 = Cesium.Cartesian3.fromDegrees(positions_latlng.lon * 1, positions_latlng.lat * 1, positions_latlng.height * 1)
    const e = Cesium.SceneTransforms.wgs84ToWindowCoordinates(this.viewer.scene, cartesian3)
    const pickObject = this._getPickObject(e, this.viewer)
    let cartesian = pickObject?.position
    if (!cartesian) return
    const cartesianPoint = Cesium.Cartographic.fromCartesian(cartesian)
    const longitude = Cesium.Math.toDegrees(cartesianPoint.longitude)
    const latitude = Cesium.Math.toDegrees(cartesianPoint.latitude)
    const height = getHeight()
    const waypoint = { longitude: longitude, latitude: latitude, height }
    cartesian = Cesium.Cartesian3.fromDegrees(longitude, latitude, height)
    const isDuplicate =
      this.waypoints.length > 0 &&
      this._isSamePoint(waypoint, this.waypoints[this.waypoints.length - 1])
    // 如果不是重复位置，才添加点
    if (!isDuplicate) {
      // 如果没有起始点，添加起始点
      if (!this._hasStartingPoint) {
        await this.addStartingPoint(cartesian)
      } else {
        const startPoint = Cesium.Cartesian3.fromDegrees(
          waypoint.longitude,
          waypoint.latitude,
          waypoint.height
        )
        this._frustumTool?.hide()
        // 新增航点
        const entity = await this.addWaypoint(
          waypoint,
          [startPoint, cartesian],
          true
        )
        let positionOption
        if (this._drawPointLine.length === 1) {
          const point = this.conversion(this._startingPoint)
          const nowPoint = this.conversion(
            this._drawPointLine[this._drawPointLine.length - 1].positions[0]
          )
          const te = getEllipsoidCartesian({
            lng: point.longitude,
            lat: point.latitude,
            h: 0,
          })
          const vs = getEllipsoidCartesian({
            lng: nowPoint.longitude * 1,
            lat: nowPoint.latitude * 1,
            h: nowPoint.height * 1,
          })
          positionOption = this.calculateCompleteEulerAnglesWithCesium(
            vs,
            te
          )
        } else {
          const point = this.conversion(
            this._drawPointLine[this._drawPointLine.length - 2].positions[0]
          )
          const nowPoint = this.conversion(
            this._drawPointLine[this._drawPointLine.length - 1].positions[0]
          )
          const te = getEllipsoidCartesian({
            lng: point.longitude,
            lat: point.latitude,
            h: point.height || 0,
          })
          const vs = getEllipsoidCartesian({
            lng: nowPoint.longitude * 1,
            lat: nowPoint.latitude * 1,
            h: nowPoint.height * 1,
          })
          positionOption = this.calculateCompleteEulerAnglesWithCesium(
            vs,
            te
          )
        }
        const frustumOption = {
          heading: 0,
          pitch: 0,
          roll: 0,
        }
        frustumOption.heading = Math.floor(
          Cesium.Math.toDegrees(positionOption.heading) + 90
        )
        frustumOption.pitch = 90
        frustumOption.roll = 0
        FRUSTUM_CONFIG.BASE_PITCH = frustumOption.pitch
        FRUSTUM_CONFIG.BASE_HEADING = frustumOption.heading + 90
        FRUSTUM_CONFIG.BASE_ROLL = frustumOption.roll
        await this.addFrustum(waypoint, entity)
        this._frustumTool?.show()
        isSave.value = true
        // 如果有起始点和至少一个航点，更新航线
        if (this._hasStartingPoint && this.waypoints.length >= 1) {
          // 删除旧的航线实体（如果存在）
          const oldRoute = this.routes.find(
            (r) => r.type === 'Route' && (r.entity as any)._isDrawing
          )
          if (oldRoute) {
            this.deleteRoute(oldRoute.id)
          }

          // 创建新的航线实体
          this.addRoute(true)
        }
      }

      if (this.store) {
        if (this.waypoints.length > 0) {
          this.store.commit(
            'SET_CURRPOINT_INFO',
            this.waypoints[this.waypoints.length - 1]
          )
        }
      }
    }
  }

  /**
   * 绘制航点航线的左击事件
   * @private
   */
  private _leftClickEventForPointLine (): void {
    this._drawHandler!.setInputAction(async (e: any) => {
      this.viewer.scene.screenSpaceCameraController.enableRotate = true
      this.viewer.scene.screenSpaceCameraController.enableLook = true
      this.viewer.scene.screenSpaceCameraController.enableInputs = true
      this.viewer.scene.screenSpaceCameraController.enableTilt = true
      this.viewer.scene.screenSpaceCameraController.enableTranslate = true
      this.viewer.scene.screenSpaceCameraController.enableZoom = true
      const pickObject = this._getPickObject(e.position, this.viewer)
      let cartesian = pickObject?.position
      if (!cartesian) return
      this.store.state.isUseGlobalHeight = true
      const cartesianPoint = Cesium.Cartographic.fromCartesian(cartesian)
      const longitude = Cesium.Math.toDegrees(cartesianPoint.longitude)
      const latitude = Cesium.Math.toDegrees(cartesianPoint.latitude)
      const height = getHeight()
      const waypoint = { longitude: longitude, latitude: latitude, height }
      cartesian = Cesium.Cartesian3.fromDegrees(longitude, latitude, height)
      // 检查是否与上一个点位置相同
      const isDuplicate =
        this.waypoints.length > 0 &&
        this._isSamePoint(waypoint, this.waypoints[this.waypoints.length - 1])

      // 如果不是重复位置，才添加点
      if (!isDuplicate) {
        // 如果没有起始点，添加起始点
        if (!this._hasStartingPoint) {
          await this.addStartingPoint(cartesian)
        } else {
          const startPoint = Cesium.Cartesian3.fromDegrees(
            waypoint.longitude,
            waypoint.latitude,
            waypoint.height
          )
          this._frustumTool?.hide()
          // 新增航点
          const entity = await this.addWaypoint(
            waypoint,
            [startPoint, cartesian],
            true
          )
          let positionOption
          if (this._drawPointLine.length === 1) {
            const point = this.conversion(this._startingPoint)
            const nowPoint = this.conversion(
              this._drawPointLine[this._drawPointLine.length - 1].positions[0]
            )
            const te = getEllipsoidCartesian({
              lng: point.longitude,
              lat: point.latitude,
              h: 0,
            })
            const vs = getEllipsoidCartesian({
              lng: nowPoint.longitude * 1,
              lat: nowPoint.latitude * 1,
              h: nowPoint.height * 1,
            })
            positionOption = this.calculateCompleteEulerAnglesWithCesium(
              vs,
              te
            )
          } else {
            const point = this.conversion(
              this._drawPointLine[this._drawPointLine.length - 2].positions[0]
            )
            const nowPoint = this.conversion(
              this._drawPointLine[this._drawPointLine.length - 1].positions[0]
            )
            const te = getEllipsoidCartesian({
              lng: point.longitude,
              lat: point.latitude,
              h: point.height || 0,
            })
            const vs = getEllipsoidCartesian({
              lng: nowPoint.longitude * 1,
              lat: nowPoint.latitude * 1,
              h: nowPoint.height * 1,
            })
            positionOption = this.calculateCompleteEulerAnglesWithCesium(
              vs,
              te
            )
          }
          const frustumOption = {
            heading: 0,
            pitch: 0,
            roll: 0,
          }
          frustumOption.heading = Math.floor(
            Cesium.Math.toDegrees(positionOption.heading) + 90
          )
          frustumOption.pitch = 90
          frustumOption.roll = 0
          FRUSTUM_CONFIG.BASE_PITCH = frustumOption.pitch
          FRUSTUM_CONFIG.BASE_HEADING = frustumOption.heading + 90
          FRUSTUM_CONFIG.BASE_ROLL = frustumOption.roll
          await this.addFrustum(waypoint, entity)
          await this.changeFrustum(
            entity.id,
            frustumOption.heading,
            frustumOption.pitch
          )
          this._frustumTool?.show()
          isSave.value = true
          // 如果有起始点和至少一个航点，更新航线
          if (this._hasStartingPoint && this.waypoints.length >= 1) {
            // 删除旧的航线实体（如果存在）
            const oldRoute = this.routes.find(
              (r) => r.type === 'Route' && (r.entity as any)._isDrawing
            )
            if (oldRoute) {
              this.deleteRoute(oldRoute.id)
            }

            // 创建新的航线实体
            this.addRoute(true)
          }
        }

        if (this.store) {
          if (this.waypoints.length > 0) {
            this.store.commit(
              'SET_CURRPOINT_INFO',
              this.waypoints[this.waypoints.length - 1]
            )
          }
        }
      }
    }, Cesium.ScreenSpaceEventType.LEFT_CLICK)
  }

  /**
   * 注册面状航线绘制事件
   */
  private _registerPolygonEvents (): void {
    this._removePolygonEvents()

    this._polygonDrawingHandler = new Cesium.ScreenSpaceEventHandler(
      this.viewer.scene.canvas
    )

    // 左键点击添加点
    this._polygonDrawingHandler.setInputAction(async (e: any) => {
      const ray = this.viewer.camera.getPickRay(e.position)
      if (!ray) return
      await this._handlePolygonClick(e.position)
    }, Cesium.ScreenSpaceEventType.LEFT_CLICK)

    // 右键点击完成绘制
    this._polygonDrawingHandler.setInputAction((e: any) => {
      const pickObject = this._getPickObject(e.position, this.viewer)
      const cartesian = pickObject?.position
      if (!cartesian) return
      this._completePolygon(cartesian)
    }, Cesium.ScreenSpaceEventType.LEFT_DOUBLE_CLICK)

    // 鼠标移动显示预览
    this._polygonDrawingHandler.setInputAction((e: any) => {
      this._handlePolygonMouseMove(e.endPosition)
    }, Cesium.ScreenSpaceEventType.MOUSE_MOVE)
  }

  /**
   * 转换位置数组为可序列化的格式
   */
  private _serializePositions (positions: Cesium.Cartesian3[]): any[] {
    if (!positions || !Array.isArray(positions)) return []

    return positions
      .map((pos) => {
        if (!pos) return null

        try {
          const cartographic = Cesium.Cartographic.fromCartesian(pos)
          if (cartographic) {
            return {
              longitude: Cesium.Math.toDegrees(cartographic.longitude),
              latitude: Cesium.Math.toDegrees(cartographic.latitude),
              height: cartographic.height,
            }
          }
        } catch (error) {
          console.warn('转换位置失败:', error)
        }

        // 如果转换失败，尝试直接获取坐标
        if (pos.x !== undefined && pos.y !== undefined && pos.z !== undefined) {
          return {
            x: pos.x,
            y: pos.y,
            z: pos.z,
          }
        }

        return null
      })
      .filter((pos) => pos !== null)
  }

  /**
   * 开始拖拽
   */
  private _startDrag (entity: PointEntity, screenPosition: any): void {
    this._isDragging = true
    this._draggingEntity = entity

    // 禁用相机控制，防止地图移动
    const controller = this.viewer.scene.screenSpaceCameraController
    controller.enableRotate = false
    controller.enableTranslate = false
    controller.enableZoom = false
    controller.enableTilt = false
    controller.enableLook = false

    // 存储拖拽开始时的位置
    this._dragStartPosition = this._getPickPosition(screenPosition)

    // 如果是可拖拽点，存储原始位置
    if (this._selectedDrawing) {
      this._originalPositions = this._serializePositions([
        ...this._selectedDrawing.positions,
      ])
    }
    this._handleDraggablePointDrag(entity, screenPosition)
    // console.log('开始拖拽:', entity.typeInfo)
  }

  private _isValidPosition (position: Cesium.Cartesian3): boolean {
    if (!position) return false

    try {
      const cartographic = Cesium.Cartographic.fromCartesian(position)
      if (!cartographic) return false

      return (
        !isNaN(cartographic.longitude) &&
        !isNaN(cartographic.latitude) &&
        !isNaN(cartographic.height) &&
        Math.abs(cartographic.latitude) <= Math.PI / 2 &&
        Math.abs(cartographic.longitude) <= Math.PI
      )
    } catch (error) {
      return false
    }
  }

  /**
   * 添加一个辅助方法来更新选中的图形位置
   */
  private _updateSelectedDrawingPositions (): void {
    if (!this._selectedDrawing) return

    const entity = this._selectedDrawing.entity as PolygonEntity
    if (!entity) return

    if (entity.polygon) {
      // 更新多边形实体
      entity.polygon.hierarchy = new Cesium.PolygonHierarchy(
        this._selectedDrawing.positions
      )
    }

    // 更新折线边界
    if (entity.polyline) {
      const closedPositions = [
        ...this._selectedDrawing.positions,
        this._selectedDrawing.positions[0],
      ]
      entity.polyline.positions = closedPositions
    }

    // 同步更新 polygonComObj 中的实体
    if (this.polygonComObj.polygonEntity) {
      this.polygonComObj.polygonEntity.polygon.hierarchy =
        new Cesium.PolygonHierarchy(this.polygonComObj.positionList)
      this.polygonComObj.polygonEntity.polyline.positions = [
        ...this.polygonComObj.positionList,
        this.polygonComObj.positionList[0],
      ]
    }
  }

  // 处理可拖拽点拖拽
  private _handleDraggablePointDrag (
    pointEntity: any,
    eventPosition: any
  ): void {
    this._isDragging = true
    this._draggingEntity = pointEntity
    this._draggingIsVirtual = false

    // 存储原始位置
    if (this._selectedDrawing) {
      this._originalPositions = [...this._selectedDrawing.positions]
    }

    // 创建拖动事件处理器
    this._setupDragEventHandlers()
  }

  // 设置拖动事件处理器
  private _setupDragEventHandlers (): void {
    if (!this._draggingEntity) return

    // 创建新的事件处理器
    const clickHandler = new Cesium.ScreenSpaceEventHandler(
      this.viewer.scene.canvas
    )
    this._draggingEntity.clickHandler = clickHandler

    // 鼠标移动事件 - 处理拖动
    clickHandler.setInputAction((moveEvent: any) => {
      if (!this._isDragging || !this._draggingEntity || !this._selectedDrawing) { return }

      // 获取新位置
      const pickedObject = this._getPickObject(
        moveEvent.endPosition,
        this.viewer
      )
      const newPosition = pickedObject?.position
      if (!newPosition) return
      const controller = this.viewer.scene.screenSpaceCameraController
      controller.enableRotate = false
      controller.enableTranslate = false
      controller.enableZoom = false
      controller.enableTilt = false
      controller.enableLook = false

      // 更新拖动点的位置
      this._draggingEntity.position = newPosition

      // 查找点的索引
      let pointIndex = -1
      if (this._draggingIsVirtual) {
        pointIndex = this._virtualPointIndex
      } else {
        pointIndex = this._draggablePoints.findIndex(
          (point) => point.id === this._draggingEntity.id
        )
      }

      if (pointIndex !== -1) {
        // 更新位置数组
        this._selectedDrawing.positions[pointIndex] = newPosition
        this.polygonComObj.pointEntityList[pointIndex].position = newPosition
        this.polygonComObj.movePositions[pointIndex] = newPosition
        this.polygonComObj.copyPositionList[pointIndex] = newPosition
        this.polygonComObj.positionList[pointIndex] = newPosition

        // 更新图形实体
        this._updateSelectedDrawingPositions()

        // 更新虚拟点位置（如果是折线或多边形）
        this._updateVirtualPoints()

        // 更新图形属性（长度、面积等）
        this._updateDrawingAttributes(this._selectedDrawing)
      }
    }, Cesium.ScreenSpaceEventType.MOUSE_MOVE)

    // 鼠标释放事件 - 结束拖动
    clickHandler.setInputAction((upEvent: any) => {
      if (this._isDragging) {
        // 清理事件处理器
        if (clickHandler && !clickHandler.isDestroyed()) {
          clickHandler.removeInputAction(
            Cesium.ScreenSpaceEventType.MOUSE_MOVE
          )
          clickHandler.removeInputAction(
            Cesium.ScreenSpaceEventType.LEFT_CLICK
          )
          clickHandler.destroy()
        }

        // 重置状态
        this._isDragging = false
        this._draggingIsVirtual = false
        this._virtualPointIndex = -1
        this._draggingEntity = null
        // 禁用相机控制，防止地图移动
        const controller = this.viewer.scene.screenSpaceCameraController
        controller.enableRotate = true
        controller.enableTranslate = true
        controller.enableZoom = true
        controller.enableTilt = true
        controller.enableLook = true
      }
    }, Cesium.ScreenSpaceEventType.LEFT_CLICK)
  }

  /**
   * 清除所有虚拟点
   */
  private _clearVirtualPoints (): void {
    this._virtualPoints.forEach((point) => {
      this._dataSource.entities.remove(point)
    })
    this._virtualPoints = []
  }

  /**
   * 更新虚拟点位置
   */
  private _updateVirtualPoints (): void {
    // 清除所有虚拟点
    this._clearVirtualPoints()

    // 为选中的航线或多边形添加虚拟点
    if (this._selectedDrawing.positions.length > 2) {
      const positions = this._selectedDrawing.positions
      for (let i = 0; i < positions.length; i++) {
        const nextIndex = (i + 1) % positions.length
        this._addVirtualPoint(i, positions[i], positions[nextIndex])
      }
    }
  }

  /**
   * 添加虚拟点
   */
  private _addVirtualPoint (
    index: number,
    startPosition: Cesium.Cartesian3,
    endPosition: Cesium.Cartesian3
  ): void {
    const midPoint = new Cesium.Cartesian3()
    Cesium.Cartesian3.add(startPosition, endPosition, midPoint)
    Cesium.Cartesian3.multiplyByScalar(midPoint, 0.5, midPoint)
    console.log(index, '_addVirtualPoint')

    const virtualPoint = this._dataSource.entities.add({
      position: midPoint,
      point: {
        pixelSize: 8,
        color: Cesium.Color.WHITE.withAlpha(0.7),
        heightReference: Cesium.HeightReference.NONE,
        disableDepthTestDistance: Number.POSITIVE_INFINITY,
        outlineColor: Cesium.Color.WHITE.withAlpha(0.7),
        outlineWidth: 1,
      },
      description: `虚拟点 ${index + 1}`,
    }) as any
    virtualPoint.typeInfo = 'polygon_new_edit'

    virtualPoint._isVirtual = true
    virtualPoint._index = index

    this._virtualPoints.push(virtualPoint)
  }

  // 更新图形属性
  private _updateDrawingAttributes (drawing: any): void {
    if (!drawing) return

    drawing.attr = {
      horizontalDis: this._calculatePolylineLength([
        ...drawing.positions,
        drawing.positions[0],
      ]),
      horizontalArea: this._calculatePolygonArea(drawing.positions),
      heightDiff: this._calculateHeightDiff(drawing.positions),
    }
  }

  /**
   * 计算折线长度
   */
  private _calculatePolylineLength (positions: Cesium.Cartesian3[]): number {
    if (!positions || positions.length < 2) return 0

    let length = 0
    for (let i = 1; i < positions.length; i++) {
      length += Cesium.Cartesian3.distance(positions[i - 1], positions[i])
    }

    return length
  }

  /**
   * 计算多边形面积
   */
  private _calculatePolygonArea (positions: Cesium.Cartesian3[]): number {
    if (!positions || positions.length < 3) return 0

    let area = 0
    const length = positions.length

    for (let i = 0; i < length; i++) {
      const j = (i + 1) % length
      const point1 = positions[i]
      const point2 = positions[j]

      area += point1.x * point2.y - point1.y * point2.x
    }

    return Math.abs(area / 2)
  }

  /**
   * 计算高度差
   */
  private _calculateHeightDiff (positions: Cesium.Cartesian3[]): number {
    if (positions.length === 0) return 0

    let minHeight = Number.MAX_VALUE
    let maxHeight = Number.MIN_VALUE

    positions.forEach((position) => {
      const cartographic = Cesium.Cartographic.fromCartesian(position)
      if (cartographic.height < minHeight) minHeight = cartographic.height
      if (cartographic.height > maxHeight) maxHeight = cartographic.height
    })

    return maxHeight - minHeight
  }

  /** 初始化航线实体 */
  private initStripPolylineEntity (): void {
    const color = Cesium.Color.fromCssColorString('#2d8cf0')
    color.alpha = 0.5
    const newColor = color.toCssColorString()
    this.stripPointComObj.polylineEntity = this._dataSource.entities.add({
      polyline: {
        positions: [],
        width: 6,
        material: new Cesium.PolylineOutlineMaterialProperty({
          color: Cesium.Color.fromCssColorString(newColor),
          outlineColor: new Cesium.Color(1, 1, 1, 1),
          outlineWidth: 3
        }),
        clampToGround: true,
        classificationType: Cesium.ClassificationType.BOTH
      },
      show: true
    })
    this.stripPointComObj.polylineEntity.attrColor = newColor
    this.stripPointComObj.pointTextEntity1 = this._dataSource.entities.add({
      parent: this.polygonComObj.polylineEntity,
      position: undefined,
      label: {
        font: '16px 黑体',
        fillColor: new Cesium.Color(1, 1, 1, 1),
        outlineColor: Cesium.Color.BLACK,
        outlineWidth: 4,
        showBackground: true,
        horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
        verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
        pixelOffset: new Cesium.Cartesian2(0, -15),
        text: '',
        scaleByDistance: new Cesium.NearFarScalar(1000, 1, 500000, 0.8),
        disableDepthTestDistance: 500000,
        scale: 1,
        style: 2,
      },
    })
  }

  /**
   * 初始化多边形实体
   */
  private initPolygonEntity (): void {
    const color = Cesium.Color.fromCssColorString('#2d8cf0')
    color.alpha = 0.5

    this.polygonComObj.polygonEntity = this._dataSource.entities.add({
      polygon: {
        hierarchy: new Cesium.PolygonHierarchy([]),
        material: color,
        classificationType: Cesium.ClassificationType.BOTH,
      },
      polyline: {
        positions: [],
        width: 6,
        material: new Cesium.PolylineOutlineMaterialProperty({
          color: Cesium.Color.fromCssColorString('#2d8cf0'),
          outlineColor: new Cesium.Color(1, 1, 1, 1),
          outlineWidth: 3,
        }),
        clampToGround: true,
        classificationType: Cesium.ClassificationType.BOTH,
      },
      show: true,
    }) as PolygonEntity

    this.polygonComObj.polygonEntity.attrColor = '#2d8cf0'
    this.polygonComObj.pointTextEntity1 = this._dataSource.entities.add({
      parent: this.polygonComObj.polygonEntity,
      position: undefined,
      label: {
        font: '16px 黑体',
        fillColor: new Cesium.Color(1, 1, 1, 1),
        outlineColor: Cesium.Color.BLACK,
        outlineWidth: 4,
        showBackground: true,
        horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
        verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
        pixelOffset: new Cesium.Cartesian2(0, -15),
        text: '',
        scaleByDistance: new Cesium.NearFarScalar(1000, 1, 500000, 0.8),
        disableDepthTestDistance: 500000,
        scale: 1,
        style: 2,
      },
    })

    this.polygonComObj.pointTextEntity2 = this._dataSource.entities.add({
      parent: this.polygonComObj.polygonEntity,
      position: undefined,
      label: {
        font: '16px 黑体',
        fillColor: new Cesium.Color(1, 1, 1, 1),
        outlineColor: Cesium.Color.BLACK,
        outlineWidth: 4,
        showBackground: true,
        horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
        verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
        pixelOffset: new Cesium.Cartesian2(0, -15),
        text: '',
        scaleByDistance: new Cesium.NearFarScalar(1000, 1, 500000, 0.8),
        disableDepthTestDistance: 500000,
        scale: 1,
        style: 2,
      },
    })
  }

  /**
 * 处理面状航线点击事件
 */
  private async _handlePolygonClick (position: Cesium.Cartesian2): Promise<void> {
    // 确保事件只处理一次
    if (this._isProcessingClick) return

    this._isProcessingClick = true

    try {
      const pickObject = this._getPickObject(position, this.viewer)
      const cartesian = pickObject?.position
      if (!cartesian) return

      // 获取高度
      const cartesianPoint = Cesium.Cartographic.fromCartesian(cartesian)
      const longitude = Cesium.Math.toDegrees(cartesianPoint.longitude)
      const latitude = Cesium.Math.toDegrees(cartesianPoint.latitude)
      const height = cartesianPoint.height

      // 检查是否与上一个点位置相同
      if (this.polygonComObj.positionList.length > 0) {
        const lastPoint = this.conversion(this.polygonComObj.positionList[this.polygonComObj.positionList.length - 1])
        const distance = this._calculateDistance(
          { longitude, latitude, height },
          lastPoint
        )

        // 如果距离太近，不添加点
        if (distance < 1.0) { // 1米阈值
          return
        }
      }

      // 如果达到最大点数，不再添加
      if (this.polygonComObj.positionList.length >= this._maxPolygonPoints) {
        message.warning(`最多只能添加${this._maxPolygonPoints}个点`)
        return
      }

      // 创建起始点
      const startPoint = Cesium.Cartesian3.fromDegrees(longitude, latitude, height)

      // 添加点到数组
      this._polygonPoints.push(cartesian)

      // 初始化多边形实体（如果还没有）
      if (!this.polygonComObj.polygonEntity) {
        this.initPolygonEntity()
      }

      // 添加位置到列表
      this.polygonComObj.positionList.push(startPoint)
      this.polygonComObj.copyPositionList.push(Cesium.Cartesian3.clone(startPoint))
      this.polygonComObj.movePositions = [...this.polygonComObj.positionList]

      // 添加视觉点
      const pointIndex = this.polygonComObj.positionList.length - 1
      const point = this._addPolygonPoint(
        startPoint,
        15,
        new Cesium.Color(1, 1, 1, 1),
        pointIndex,
        'polygon_new',
        this.polygonComObj.polygonEntity!
      ) as PointEntity

      this.polygonComObj.pointEntityList.push(point)

      // 更新点显示状态
      this._updatePointDisplayStates()

      // 更新多边形几何
      this._updatePolygonGeometry()

      // 如果达到最大点数，自动完成多边形
      if (this.polygonComObj.positionList.length === this._maxPolygonPoints) {
        setTimeout(() => {
          this._completePolygon()
        }, 100)
      }
    } finally {
      this._isProcessingClick = false
    }
  }

  /**
 * 更新点显示状态
 */
  private _updatePointDisplayStates (): void {
    const pointCount = this.polygonComObj.pointEntityList.length

    if (pointCount > 2) {
      // 隐藏最后一个点的billboard，显示倒数第二个点的point
      this.polygonComObj.pointEntityList[pointCount - 1].billboard!.show = false
      this.polygonComObj.pointEntityList[pointCount - 1].point!.show = true

      this.polygonComObj.pointEntityList[pointCount - 2].billboard!.show = false
      this.polygonComObj.pointEntityList[pointCount - 2].point!.show = true
    }

    // 第一个点显示billboard
    if (pointCount > 0) {
      this.polygonComObj.pointEntityList[0].billboard!.show = true
    }
  }

  /**
 * 更新多边形几何
 */
  private _updatePolygonGeometry (): void {
    if (!this.polygonComObj.polygonEntity) return

    // 更新多边形hierarchy
    this.polygonComObj.polygonEntity.polygon!.hierarchy = new Cesium.CallbackProperty(() => {
      return new Cesium.PolygonHierarchy(this.polygonComObj.positionList)
    }, false)

    // 更新折线
    if (this.polygonComObj.positionList.length > 1) {
      this.polygonComObj.polygonEntity.polyline!.positions = new Cesium.CallbackProperty(() => {
        const positions = [...this.polygonComObj.positionList]
        if (positions.length > 0) {
          positions.push(positions[0]) // 闭合多边形
        }
        return positions
      }, false)
    }
  }

  /**
 * 计算两点距离
 */
  private _calculateDistance (point1: any, point2: any): number {
    const dx = point1.longitude - point2.longitude
    const dy = point1.latitude - point2.latitude
    const dz = point1.height - point2.height

    return Math.sqrt(dx * dx + dy * dy + dz * dz)
  }

  /**
   * 添加多边形点
   */
  private _addPolygonPoint (
    position: Cesium.Cartesian3,
    pixelSize: number,
    color: Cesium.Color,
    pointIndex: number,
    typeInfo: string,
    parent: Cesium.Entity
  ): PointEntity {
    const entity = this._dataSource.entities.add({
      position: position,
      point: {
        pixelSize: pixelSize,
        color: color,
        disableDepthTestDistance: Number.POSITIVE_INFINITY,
        heightReference: Cesium.HeightReference.NONE,
        outlineColor: Cesium.Color.WHITE,
        outlineWidth: 2,
        show: true,
      },
      billboard: {
        image:
          'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAYAQMAAADJbu9PAAAAAXNSR0IB2cksfwAAAAlwSFlzAAALEwAACxMBAJqcGAAAAAZQTFRFAAAAW/tlDx7VPwAAAAJ0Uk5TAP9bkSK1AAAAJElEQVR4nGNkrGdk3svIfpOR/yGj/E1G+32M9fWM/xlAgqQgALk7Exo5KkuiAAAAAElFTkSuQmCC',
        show: false,
        scale: 0.35,
        disableDepthTestDistance: Number.POSITIVE_INFINITY,
        heightReference: Cesium.HeightReference.NONE,
      },
    }) as PointEntity

    entity.pointIndex = pointIndex
    entity.typeInfo = typeInfo
    entity.parent = parent

    return entity
  }

  /**
   * 处理鼠标移动事件（预览下一个点）
   */
  private _handlePolygonMouseMove (position: Cesium.Cartesian2): void {
    if (
      this.polygonComObj.pointEntityList.length === 0 ||
      this.polygonComObj.pointEntityList.length >= this._maxPolygonPoints
    ) {
      return
    }
    const pickObject = this._getPickObject(position, this.viewer)
    const cartesian = pickObject?.position
    if (!cartesian) return
    // 获取高度
    const cartesianPoint = Cesium.Cartographic.fromCartesian(cartesian)
    const longitude = Cesium.Math.toDegrees(cartesianPoint.longitude)
    const latitude = Cesium.Math.toDegrees(cartesianPoint.latitude)
    const height = cartesianPoint.height
    const waypoint = { longitude: longitude, latitude: latitude, height }
    const positions = Cesium.Cartesian3.fromDegrees(
      waypoint.longitude,
      waypoint.latitude,
      waypoint.height
    )
    if (this.polygonComObj.positionList.length > 0 && positions) {
      this.polygonComObj.polygonEntity!.polygon!.hierarchy =
        new Cesium.CallbackProperty(() => {
          if (
            this.polygonComObj.movePositions.length ===
            this.polygonComObj.positionList.length + 1
          ) {
            this.polygonComObj.movePositions.pop()
          }
          this.polygonComObj.movePositions.push(positions!)
          return new Cesium.PolygonHierarchy(this.polygonComObj.movePositions)
        }, false)

      this.polygonComObj.polygonEntity!.polyline!.positions =
        new Cesium.CallbackProperty(() => {
          if (
            this.polygonComObj.movePositions.length ===
            this.polygonComObj.positionList.length + 1
          ) {
            this.polygonComObj.movePositions.pop()
          }
          this.polygonComObj.movePositions.push(positions!)
          return [
            ...this.polygonComObj.movePositions,
            this.polygonComObj.movePositions[0],
          ]
        }, false)
    }
    if (this.polygonComObj.positionList.length > 1 && positions) {
      const firstPosition = this.polygonComObj.positionList[0]
      const lastPosition =
        this.polygonComObj.positionList[
          this.polygonComObj.positionList.length - 1
        ]

      const distance1 = this.calcDis(positions, firstPosition)
      const distance2 = this.calcDis(positions, lastPosition)

      const centerPosition1 = this.calcCenterPosition(positions, firstPosition)
      const centerPosition2 = this.calcCenterPosition(positions, lastPosition)

      this.polygonComObj.pointTextEntity1!.label!.text = distance1
      this.polygonComObj.pointTextEntity2!.label!.text = distance2

      this.polygonComObj.pointTextEntity1!.position = centerPosition1
      this.polygonComObj.pointTextEntity2!.position = centerPosition2
    }
  }

  /**
   * 完成多边形绘制
   */
  private _completePolygon (pos?: Cartesian3): void {
    if (this.polygonComObj.pointEntityList.length < 3) {
      console.warn('至少需要3个点才能构成多边形')
      return
    }
    const cartesian = pos
    // 获取高度
    const cartesianPoint = Cesium.Cartographic.fromCartesian(cartesian)
    const longitude = Cesium.Math.toDegrees(cartesianPoint.longitude)
    const latitude = Cesium.Math.toDegrees(cartesianPoint.latitude)
    const height = cartesianPoint.height
    const waypoint = { longitude: longitude, latitude: latitude, height }
    const positions = Cesium.Cartesian3.fromDegrees(
      waypoint.longitude,
      waypoint.latitude,
      waypoint.height
    )

    // 添加视觉点
    const point = this._addPolygonPoint(
      positions,
      15,
      new Cesium.Color(1, 1, 1, 1),
      this.polygonComObj.positionList.length - 1,
      'polygon_new',
      this.polygonComObj.polygonEntity!
    ) as PointEntity
    this.polygonComObj.pointEntityList.push(point)
    if (this.polygonComObj.positionList.length > 0 && positions) {
      this.polygonComObj.polygonEntity!.polygon!.hierarchy =
        new Cesium.CallbackProperty(() => {
          if (
            this.polygonComObj.movePositions.length ===
            this.polygonComObj.positionList.length + 1
          ) {
            this.polygonComObj.movePositions.pop()
          }
          this.polygonComObj.movePositions.push(positions)
          return new Cesium.PolygonHierarchy(this.polygonComObj.movePositions)
        }, false)

      this.polygonComObj.polygonEntity!.polyline!.positions =
        new Cesium.CallbackProperty(() => {
          if (
            this.polygonComObj.movePositions.length ===
            this.polygonComObj.positionList.length + 1
          ) {
            this.polygonComObj.movePositions.pop()
          }
          this.polygonComObj.movePositions.push(positions)
          return [
            ...this.polygonComObj.movePositions,
            this.polygonComObj.movePositions[0],
          ]
        }, false)
    }
    if (
      this.polygonComObj.positionList > 0 &&
      this.polygonComObj.positionList[0] !==
      this.polygonComObj.positionList[
        this.polygonComObj.positionList.length - 1
      ]
    ) {
      this.polygonComObj.positionList.push(this.polygonComObj.positionList[0])
    }
    console.log(
      'polygonComplete',
      '_completePolygon',
      this.polygonComObj.positionList
    )

    emitter.emit('polygonComplete', this.polygonComObj.positionList)
    this._selectedDrawing = {
      entity: this.polygonComObj.polygonEntity!,
      positions: [...this.polygonComObj.positionList],
    }
    this._removePolygonEvents()
    this.updateVirtual(this.polygonComObj)
    this._initSelection()
  }

  // 添加一个公共方法来更新多边形数据
  public updateVirtual (comObj): void {
    this._clearVirtualPoints()
    if (comObj.positionList.length > 0) {
      for (
        let index = 0;
        index < comObj.positionList.length;
        index++
      ) {
        const nextIndex = (index + 1) % comObj.positionList.length
        this._addVirtualPoint(
          index,
          comObj.positionList[index],
          comObj.positionList[nextIndex]
        )
      }
    }
  }

  // 在类中添加一个方法来手动触发图形更新
  public forceUpdatePolygon (): void {
    if (this.polygonComObj.polygonEntity) {
      // 强制更新多边形几何
      this.polygonComObj.polygonEntity.polygon.hierarchy =
        new Cesium.PolygonHierarchy(this.polygonComObj.positionList)

      // 强制更新折线
      if (this.polygonComObj.positionList.length > 1) {
        this.polygonComObj.polygonEntity.polyline.positions = [
          ...this.polygonComObj.positionList,
          this.polygonComObj.positionList[0],
        ]
      }
    }
  }

  initSelection () {
    if (this._selectHandler) {
      this._selectHandler.destroy()
      this._selectHandler = null
    }
    this.updateVirtual(this.polygonComObj)
    this._initSelection()
  }

  /**
 * 初始化选择功能
 */
  private _initSelection (): void {
    if (this._selectHandler) {
      this._selectHandler.destroy()
      this._selectHandler = null
    }

    this._selectHandler = new Cesium.ScreenSpaceEventHandler(this.viewer.scene.canvas)

    // 鼠标按下开始拖拽
    this._selectHandler.setInputAction((event: any) => {
      if (this._isDragging) {
        return
      }

      const pickObject = this._getPickObject(event.position, this.viewer)
      if (!pickObject?.pickedObject?.id) return

      const pickedId = pickObject.pickedObject.id as PointEntity

      // 检查是否是编辑点、虚拟点、顶点或图形本身
      if (pickedId.typeInfo) {
        // 如果是多边形相关实体
        if (pickedId.typeInfo.includes('_new_edit') ||
          pickedId.typeInfo.includes('_new') ||
          pickedId.typeInfo === 'polygon_new_edit') {
          // 确保选中了当前绘制的多边形
          if (!this._selectedDrawing && this.polygonComObj.polygonEntity) {
            this._selectedDrawing = {
              entity: this.polygonComObj.polygonEntity,
              positions: [...this.polygonComObj.positionList]
            }
          }

          // 设置拖动状态
          this._isDragging = true
          this._draggingEntity = pickedId
          this._draggingIsVirtual = pickedId.typeInfo.includes('_edit') ||
            pickedId._isVirtual === true

          // 记录虚拟点索引
          if (this._draggingIsVirtual) {
            this._virtualPointIndex = pickedId._index || -1
          }

          // 存储原始位置
          if (this._selectedDrawing) {
            this._originalPositions = [...this._selectedDrawing.positions]
          }

          // 禁用相机控制
          const controller = this.viewer.scene.screenSpaceCameraController
          controller.enableRotate = false
          controller.enableTranslate = false
          controller.enableZoom = false
          controller.enableTilt = false
          controller.enableLook = false

          // 记录拖拽起始位置
          this._dragStartPosition = this._getPickPosition(event.position)
        }
      }
    }, Cesium.ScreenSpaceEventType.LEFT_DOWN)

    // 鼠标移动处理拖拽
    this._selectHandler.setInputAction((event: any) => {
      if (!this._isDragging || !this._draggingEntity) return

      this._handleDragMove(event.endPosition)
    }, Cesium.ScreenSpaceEventType.MOUSE_MOVE)

    // 鼠标释放结束拖拽
    this._selectHandler.setInputAction((event: any) => {
      if (this._isDragging) {
        this._endDrag()
      }
    }, Cesium.ScreenSpaceEventType.LEFT_UP)

    // 鼠标点击其他地方取消拖拽
    this._selectHandler.setInputAction((event: any) => {
      // 如果正在拖拽，结束拖拽
      if (this._isDragging) {
        this._endDrag()
      }
    }, Cesium.ScreenSpaceEventType.LEFT_CLICK)
  }

  /**
 * 结束拖拽
 */
  private _endDrag (): void {
    if (!this._isDragging) return

    // 恢复相机控制
    const controller = this.viewer.scene.screenSpaceCameraController
    controller.enableRotate = true
    controller.enableTranslate = true
    controller.enableZoom = true
    controller.enableTilt = true
    controller.enableLook = true

    // 如果是虚拟点拖拽，删除虚拟点实体
    if (this._draggingIsVirtual && this._draggingEntity) {
      this._dataSource.entities.remove(this._draggingEntity)

      // 从虚拟点数组中移除
      const index = this._virtualPoints.findIndex(vp => vp.id === this._draggingEntity?.id)
      if (index !== -1) {
        this._virtualPoints.splice(index, 1)
      }
    }

    // 触发多边形更新事件
    if (this.polygonComObj.positionList.length > 0) {
      emitter.emit('polygonComplete', this.polygonComObj.positionList)
    }

    // 重置拖拽状态
    this._isDragging = false
    this._draggingEntity = null
    this._draggingIsVirtual = false
    this._dragStartPosition = null
    this._virtualPointIndex = -1
  }

  /**
 * 处理拖拽移动
 */
  private _handleDragMove (screenPosition: any): void {
    if (!this._isDragging || !this._draggingEntity) return

    // 获取新的位置
    const pickObject = this._getPickObject(screenPosition, this.viewer)
    const newPosition = pickObject?.position
    if (!newPosition) return

    // 更新拖动实体的位置
    this._draggingEntity.position = newPosition

    // 根据拖动实体的类型执行不同的更新逻辑
    if (this._draggingIsVirtual) {
      // 虚拟点拖拽 - 插入新顶点
      this._handleVirtualPointDrag(newPosition)
    } else {
      // 顶点拖拽 - 更新现有顶点
      this._handleVertexDrag(newPosition)
    }
  }

  /**
 * 处理虚拟点拖拽
 */
  private _handleVirtualPointDrag (newPosition: Cesium.Cartesian3): void {
    if (!this._selectedDrawing || this._virtualPointIndex === -1) return

    // 在虚拟点对应的边上插入新顶点
    const startIndex = this._virtualPointIndex
    const endIndex = (this._virtualPointIndex + 1) % this.polygonComObj.positionList.length

    // 插入新顶点
    this.polygonComObj.positionList.splice(endIndex, 0, newPosition)
    this.polygonComObj.copyPositionList.splice(endIndex, 0, newPosition)
    this.polygonComObj.movePositions = [...this.polygonComObj.positionList]

    // 添加对应的点实体
    const point = this._addPolygonPoint(
      newPosition,
      15,
      new Cesium.Color(1, 1, 1, 1),
      endIndex,
      'polygon_new',
      this.polygonComObj.polygonEntity!
    ) as PointEntity

    this.polygonComObj.pointEntityList.splice(endIndex, 0, point)

    // 更新所有点的索引
    this._updatePointIndices()

    // 更新图形
    this._updateSelectedDrawingPositions()

    // 清除并重新生成虚拟点
    this._clearVirtualPoints()
    this._updateVirtualPoints()
  }

  /**
 * 处理顶点拖拽
 */
  private _handleVertexDrag (newPosition: Cesium.Cartesian3): void {
    if (!this._selectedDrawing) return

    // 查找拖拽的顶点索引
    const pointIndex = this.polygonComObj.pointEntityList.findIndex(
      point => point.id === this._draggingEntity?.id
    )

    if (pointIndex !== -1) {
      // 更新位置数组
      this.polygonComObj.positionList[pointIndex] = newPosition
      this.polygonComObj.copyPositionList[pointIndex] = newPosition
      this.polygonComObj.movePositions = [...this.polygonComObj.positionList]

      // 更新点实体位置
      this.polygonComObj.pointEntityList[pointIndex].position = newPosition

      // 更新图形
      this._updateSelectedDrawingPositions()

      // 更新虚拟点位置
      this._clearVirtualPoints()
      this._updateVirtualPoints()
    }
  }

  /**
 * 更新所有点的索引
 */
  private _updatePointIndices (): void {
    this.polygonComObj.pointEntityList.forEach((point, index) => {
      point.pointIndex = index
    })
  }

  /**
   * 更新被拖拽的实体
   */
  private _updateDraggedEntity (newPosition: Cesium.Cartesian3): void {
    if (!this._draggingEntity || !this._selectedDrawing) return

    const entity = this._draggingEntity
    const drawing = this._selectedDrawing

    // 查找点的索引
    let pointIndex = -1

    // 检查是否是 polygonComObj 中的点
    if (
      this.polygonComObj.pointEntityList &&
      this.polygonComObj.pointEntityList.length > 0
    ) {
      const pointEntityIndex = this.polygonComObj.pointEntityList.findIndex(
        (point) => point.id === entity.id
      )
      if (pointEntityIndex !== -1) {
        pointIndex = pointEntityIndex
      }
    }

    if (pointIndex !== -1) {
      // 更新位置数组
      drawing.positions[pointIndex] = newPosition

      // 同步更新 polygonComObj 中的位置数据
      this.polygonComObj.positionList[pointIndex] = newPosition
      this.polygonComObj.copyPositionList[pointIndex] = newPosition
      this.polygonComObj.movePositions = [...this.polygonComObj.positionList]

      // 更新图形实体
      this._updateSelectedDrawingPositions()
    }
  }

  /**
   * 清除多边形绘制
   */
  clearPolygonDrawing (): void {
    this._removePolygonEvents()
    if (this.polygonComObj) {
      this._dataSource.entities.remove(this.polygonComObj.polygonEntity!)
      this._dataSource.entities.remove(this.polygonComObj.pointTextEntity1!)
      this._dataSource.entities.remove(this.polygonComObj.pointTextEntity2!)
      this._dataSource.entities.remove(this.polygonComObj.polylineEntity!)
      this.polygonComObj.pointEntityList.forEach((el) => {
        this._dataSource.entities.remove(el)
      })
      this.polygonComObj.textEntityList.forEach((el) => {
        this._dataSource.entities.remove(el)
      })
      this.polygonComObj.editPointEntityList.forEach((el) => {
        this._dataSource.entities.remove(el)
      })
      this.polygonComObj.entityList.forEach((el) => {
        this._dataSource.entities.remove(el)
      })
      this.polygonComObj.positionList = []
      this.polygonComObj.copyPositionList = []
      this.polygonComObj.movePositions = []
      this.polygonComObj.polygonEntity = null
      this.polygonComObj.pointTextEntity1 = null
      this.polygonComObj.pointTextEntity2 = null
      this.polygonComObj.pointEntityList = []
      this.polygonComObj.textEntityList = []
      this.polygonComObj.editPointEntityList = []
      this.polygonComObj.entityList = []
    }
  }

  /**
   * 计算距离
   */
  private calcDis (
    position1: Cesium.Cartesian3,
    position2: Cesium.Cartesian3
  ): string {
    const distance = Cesium.Cartesian3.distance(position1, position2)
    let formattedDistance = distance.toFixed(1)

    if (distance > 10000) {
      formattedDistance = `${(distance / 1000).toFixed(1)}km`
    } else {
      formattedDistance = `${formattedDistance}m`
    }

    return formattedDistance
  }

  /**
   * 计算中心位置
   */
  private calcCenterPosition (
    position1: Cesium.Cartesian3,
    position2: Cesium.Cartesian3
  ): Cesium.Cartesian3 {
    return new Cesium.Cartesian3(
      (position1.x + position2.x) / 2,
      (position1.y + position2.y) / 2,
      (position1.z + position2.z) / 2
    )
  }

  /**
   * 移除多边形事件
   */
  private _removePolygonEvents (): void {
    if (this._polygonDrawingHandler) {
      this._polygonDrawingHandler.removeInputAction(
        Cesium.ScreenSpaceEventType.LEFT_CLICK
      )
      this._polygonDrawingHandler.removeInputAction(
        Cesium.ScreenSpaceEventType.LEFT_DOUBLE_CLICK
      )
      this._polygonDrawingHandler.removeInputAction(
        Cesium.ScreenSpaceEventType.MOUSE_MOVE
      )
      this._polygonDrawingHandler.destroy()
      this._polygonDrawingHandler = null
    }
  }

  /**
   * 获取所有多边形航线
   */
  getPolygonRoutes (): PolygonRouteEntity[] {
    return this.polygonComObj.positionList
  }

  /** 获取带状航线 */
  getStripRoutes () {
    return this.stripPointComObj.positionList
  }

  getStripPolygonRoutes () {
    return this.stripPointComObj.polygonEntity?.polygon?.hierarchy?.getValue(Cesium.JulianDate.now())
  }

  /**
 * 激活带状航线绘制模式（先画中心线，完成后自动生成带状多边形）
 */
  activateStripDrawing (): void {
    this.clearSelection()
    this.clearCurrentDrawing()
    this.stopStripDrawing()
    this._isDrawingStripCenterLine = true
    this._registerStripCenterLineEvents()
  }

  /**
 * 停止当前带状中心线的绘制（清除未完成的临时点线）
 */
  stopStripDrawing (): void {
    this._removeStripCenterLineEvents()
    this._clearStripTempCenterLine()
    this._isDrawingStripCenterLine = false
  }

  /**
 * 清除所有已完成的带状航线（包括多边形、中心线、点）
 */
  clearAllStripRoutes (): void {
    this.stopStripDrawing()
    this._stripRoutes.forEach(route => {
      if (route.polygonEntity) {
        this._dataSource.entities.remove(route.polygonEntity)
      }
      if (route.polylineEntity) {
        this._dataSource.entities.remove(route.polylineEntity)
      }
      route.centerPointEntities.forEach((entity: Cesium.Entity) => {
        this._dataSource.entities.remove(entity)
      })
      this._clearStripVirtualPoints(route) // 清除虚拟点
    })

    this._stripRoutes = []
    this.stripPointComObj.copyPositionList = []
    this.stripPointComObj.positionList = []
    this.stripPointComObj.movePositions = []
    this.stripPointComObj.pointEntityList = []
    this.stripPointComObj.textEntityList = []
    this.stripPointComObj.editPointEntityList = []
    this.stripPointComObj.entityList = []
    this.stripPointComObj.polygonEntity = null
    this.stripPointComObj.polylineEntity = null
    this.stripPointComObj.pointTextEntity1 = null
    this.stripPointComObj.pointTextEntity2 = null
  }

  /**
 * 设置带状左右扩展距离（单位：米）
 * 注意：仅影响后续生成的带状航线，不影响已生成的
 */
  setStripExpandDistances (left: number, right: number): void {
    if (typeof currentStripRouteSettingData !== 'undefined') {
      currentStripRouteSettingData.leftExpandDis = left
      currentStripRouteSettingData.rightExpandDis = right
    }
  }

  /**
 * 注册带状中心线绘制事件
 */
  private _registerStripCenterLineEvents (): void {
    this._removeStripCenterLineEvents()
    const handler = new Cesium.ScreenSpaceEventHandler(this.viewer.scene.canvas)
    this._stripDrawingHandler = handler

    handler.setInputAction(async (e: any) => {
      await this._handleStripCenterLineClick(e.position)
    }, Cesium.ScreenSpaceEventType.LEFT_CLICK)

    handler.setInputAction(() => {
      this._completeStripCenterLine()
    }, Cesium.ScreenSpaceEventType.LEFT_DOUBLE_CLICK)

    handler.setInputAction((e: any) => {
      this._handleStripCenterLineMouseMove(e.endPosition)
    }, Cesium.ScreenSpaceEventType.MOUSE_MOVE)
  }

  /**
 * 移除带状中心线事件
 */
  private _removeStripCenterLineEvents (): void {
    if (this._stripDrawingHandler) {
      this._stripDrawingHandler.removeInputAction(Cesium.ScreenSpaceEventType.LEFT_CLICK)
      this._stripDrawingHandler.removeInputAction(Cesium.ScreenSpaceEventType.LEFT_DOUBLE_CLICK)
      this._stripDrawingHandler.removeInputAction(Cesium.ScreenSpaceEventType.MOUSE_MOVE)
      this._stripDrawingHandler.destroy()
      this._stripDrawingHandler = null
    }
  }

  /**
 * 清除临时绘制的中心线和点
 */
  private _clearStripTempCenterLine (): void {
    if (this.stripPointComObj.polylineEntity!) {
      this._dataSource.entities.remove(this.stripPointComObj.polylineEntity!)
      this.stripPointComObj.polylineEntity = null
    }
    if (this.stripPointComObj.pointTextEntity1) {
      this._dataSource.entities.remove(this.stripPointComObj.pointTextEntity1)
      this.stripPointComObj.pointTextEntity1 = null
    }
    if (this.stripPointComObj.pointTextEntity2) {
      this._dataSource.entities.remove(this.stripPointComObj.pointTextEntity2)
      this.stripPointComObj.pointTextEntity2 = null
    }
    this.stripPointComObj.entityList.forEach(entity => {
      this._dataSource.entities.remove(entity)
    })
    this.stripPointComObj.pointEntityList.forEach(entity => {
      this._dataSource.entities.remove(entity)
    })
    this.stripPointComObj.movePositions = []
    this.stripPointComObj.pointEntityList = []
    this.stripPointComObj.entityList = []
    this.stripPointComObj.textEntityList = []
    this.stripPointComObj.editPointEntityList = []
    this.stripPointComObj.polygonEntity = null
    this.stripPointComObj.polylineEntity = null
    this.stripPointComObj.pointTextEntity1 = null
    this.stripPointComObj.pointTextEntity2 = null
    this.stripPointComObj.positionList = []
    this._stripRoutes = []
  }

  /**
 * 处理中心线左键点击（添加中心点）
 */
  private async _handleStripCenterLineClick (screenPosition: Cesium.Cartesian2): Promise<void> {
    if (this._isDrawingStripCenterLine) {
      const pickObject = this._getPickObject(screenPosition, this.viewer)
      const cartesian = pickObject?.position
      if (!cartesian) return
      // 获取高度
      const cartesianPoint = Cesium.Cartographic.fromCartesian(cartesian)
      const longitude = Cesium.Math.toDegrees(cartesianPoint.longitude)
      const latitude = Cesium.Math.toDegrees(cartesianPoint.latitude)
      const height = cartesianPoint.height
      // 检查是否与上一个点位置相同
      if (this.stripPointComObj.positionList.length > 0) {
        const lastPoint = this.conversion(this.stripPointComObj.positionList[this.stripPointComObj.positionList.length - 1])
        const distance = this._calculateDistance(
          { longitude, latitude, height },
          lastPoint
        )

        // 如果距离太近，不添加点
        if (distance < 1.0) { // 1米阈值
          return
        }
      }
      const startPoint = Cesium.Cartesian3.fromDegrees(longitude, latitude, height)
      this._stripCenterPoints.push(cartesian)
      if (this.stripPointComObj.positionList.length === 0) {
        this.initStripPolylineEntity()
      }
      this.stripPointComObj.positionList.push(startPoint)
      this.stripPointComObj.copyPositionList.push(Cesium.Cartesian3.clone(startPoint))
      this.stripPointComObj.movePositions = [...this.stripPointComObj.positionList]
      if (!(this.stripPointComObj.polylineEntity!.polyline!.positions instanceof Cesium.CallbackProperty)) {
        this.stripPointComObj.polylineEntity!.polyline!.positions = new Cesium.CallbackProperty(
          () => this.stripPointComObj.positionList,
          false
        )
      }
      const pointIndex = this.stripPointComObj.positionList.length - 1
      const point = this._addPolygonPoint(
        startPoint,
        15,
        new Cesium.Color(1, 1, 1, 1),
        pointIndex,
        'start_point_new',
        this.stripPointComObj.polylineEntity!
      ) as PointEntity
      this.stripPointComObj.pointEntityList.push(point)
      if (this.stripPointComObj.pointEntityList.length > 1) {
        this.stripPointComObj.pointEntityList[this.stripPointComObj.pointEntityList.length - 1].point!.show = true
        this.stripPointComObj.pointEntityList[this.stripPointComObj.pointEntityList.length - 1].billboard!.show = true
        this.stripPointComObj.pointEntityList[this.stripPointComObj.pointEntityList.length - 2].billboard!.show = false
      }
      if (this.stripPointComObj.positionList.length > 1) {
        const lastPosition = this.stripPointComObj.positionList[this.stripPointComObj.positionList.length - 1]
        const secondLastPosition = this.stripPointComObj.positionList[this.stripPointComObj.positionList.length - 2]
        const distance = this.calcDis(lastPosition, secondLastPosition)
        const centerPosition = this.calcCenterPosition(lastPosition, secondLastPosition)
        const text = this.addText(centerPosition, distance)

        text.parent = this.stripPointComObj.polylineEntity
        this.stripPointComObj.textEntityList.push(text)
      }
    }
  }

  /**
   * 添加文本
   */
  private addText (position: Cesium.Cartesian3, text: string): Cesium.Entity {
    return this._dataSource.entities.add({
      position: position,
      label: {
        font: '16px 黑体',
        fillColor: new Cesium.Color(1, 1, 1, 1),
        outlineColor: Cesium.Color.BLACK,
        outlineWidth: 4,
        showBackground: true,
        horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
        verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
        pixelOffset: new Cesium.Cartesian2(0, -5),
        text: text,
        scaleByDistance: new Cesium.NearFarScalar(1000, 1, 500000, 0.8),
        disableDepthTestDistance: 500000,
        scale: 1,
        style: 2
      }
    })
  }

  /**
 * 鼠标移动时，显示从最后一个点到鼠标位置的临时连线
 */
  private _handleStripCenterLineMouseMove (screenPosition: Cesium.Cartesian2): void {
    if (this.stripPointComObj.positionList.length === 0) return
    const pickObject = this._getPickObject(screenPosition, this.viewer)
    const cartesian = pickObject?.position
    if (!cartesian) return
    // 获取高度
    const cartesianPoint = Cesium.Cartographic.fromCartesian(cartesian)
    const longitude = Cesium.Math.toDegrees(cartesianPoint.longitude)
    const latitude = Cesium.Math.toDegrees(cartesianPoint.latitude)
    const height = cartesianPoint.height
    const waypoint = { longitude: longitude, latitude: latitude, height }
    const positions = Cesium.Cartesian3.fromDegrees(
      waypoint.longitude,
      waypoint.latitude,
      waypoint.height
    )
    if (this.stripPointComObj.positionList.length > 0 && screenPosition) {
      const precisePosition = positions
      this.stripPointComObj.polylineEntity!.polyline!.positions = new Cesium.CallbackProperty(() => {
        if (this.stripPointComObj.movePositions.length === this.stripPointComObj.positionList.length + 1) {
          this.stripPointComObj.movePositions.pop()
        }
        this.stripPointComObj.movePositions.push(precisePosition)
        return this.stripPointComObj.movePositions
      }, false)
      const lastFixedPosition = this.stripPointComObj.positionList[this.stripPointComObj.positionList.length - 1]
      const distance = this.calcDis(lastFixedPosition, precisePosition)
      if (this.stripPointComObj.pointTextEntity1) {
        this.stripPointComObj.pointTextEntity1.position = precisePosition
        this.stripPointComObj.pointTextEntity1.label.text = distance
      }
    }
  }

  /**
   * 完成带状中心线绘制，生成带状多边形并启用编辑
   */
  private _completeStripCenterLine (): void {
    if (this.stripPointComObj.positionList.length < 2) {
      console.warn('至少需要2个点才能生成带状航线')
      return
    }

    let leftDis = (currentStripRouteSettingData?.leftExpandDis ?? 25) * 1
    let rightDis = (currentStripRouteSettingData?.rightExpandDis ?? 25) * 1
    if (currentStripRouteSettingData.stripRouteType === ROUTE_TYPE.ONE_ROUTE) {
      leftDis = (currentStripRouteSettingData?.allExpandDis ?? 25) * 1
      rightDis = (currentStripRouteSettingData?.allExpandDis ?? 25) * 1
    }
    const polygonPositions = this._generateStripPolygon(this.stripPointComObj.positionList, leftDis, rightDis)
    if (polygonPositions.length < 3) {
      console.warn('无法生成有效的带状多边形')
      return
    }

    // 移除临时预览标签（pointTextEntity1 和 pointTextEntity2），不再误删正常的距离标签
    if (this.stripPointComObj.pointTextEntity1) {
      this._dataSource.entities.remove(this.stripPointComObj.pointTextEntity1)
      this.stripPointComObj.pointTextEntity1 = null
    }
    if (this.stripPointComObj.pointTextEntity2) {
      this._dataSource.entities.remove(this.stripPointComObj.pointTextEntity2)
      this.stripPointComObj.pointTextEntity2 = null
    }

    // 固定中心线位置（不再使用临时预览）
    if (this.stripPointComObj.polylineEntity) {
      this.stripPointComObj.polylineEntity.polyline.positions = this.stripPointComObj.positionList
    }

    const color = Cesium.Color.fromCssColorString('#2d8cf0')
    color.alpha = 0.5
    const polygonEntity = this._dataSource.entities.add({
      polygon: {
        hierarchy: new Cesium.PolygonHierarchy(polygonPositions),
        material: color,
        classificationType: Cesium.ClassificationType.BOTH,
      },
      polyline: {
        positions: polygonPositions,
        width: 6,
        material: new Cesium.PolylineOutlineMaterialProperty({
          color: Cesium.Color.fromCssColorString('#2d8cf0'),
        }),
        clampToGround: true,
        classificationType: Cesium.ClassificationType.BOTH,
      }
    })
    polygonEntity.typeInfo = 'strip_polygon'
    this.stripPointComObj.polygonEntity = polygonEntity
    // 构建完整的 route 对象
    const stripRoute = {
      id: `strip_${Date.now()}`,
      polygonEntity: polygonEntity,
      polygonPositions: polygonPositions,
      polylineEntity: this.stripPointComObj.polylineEntity,
      centerPoints: [...this.stripPointComObj.positionList],
      centerPointEntities: [...this.stripPointComObj.pointEntityList],
      leftExpandDis: leftDis,
      rightExpandDis: rightDis,
      textEntityList: this.stripPointComObj.textEntityList, // 存储距离文本实体
    }

    // 生成距离标签（会先清除旧的）
    this._updateStripDistanceLabels(stripRoute)

    this._stripRoutes.push(stripRoute)
    this._isDrawingStripCenterLine = false
    this._removeStripCenterLineEvents()
    emitter.emit('updateStripPoint', { polygonPositions: stripRoute.polygonPositions, polylineList: stripRoute.centerPoints })
    // 启用编辑（包含虚拟点）
    this._enableStripEdit(stripRoute)
  }

  /**
 * 生成带状多边形顶点（支持左右不等宽）
 */
  private _generateStripPolygon (centerPoints: Cesium.Cartesian3[], leftDis: number, rightDis: number): Cesium.Cartesian3[] {
    if (centerPoints.length < 2) return []

    const leftPoints: Cesium.Cartesian3[] = []
    const rightPoints: Cesium.Cartesian3[] = []

    for (let i = 0; i < centerPoints.length; i++) {
      const prev = centerPoints[Math.max(0, i - 1)]
      const curr = centerPoints[i]
      const next = centerPoints[Math.min(centerPoints.length - 1, i + 1)]

      let dir: Cesium.Cartesian3
      if (i === 0) {
        dir = Cesium.Cartesian3.subtract(next, curr, new Cesium.Cartesian3())
      } else if (i === centerPoints.length - 1) {
        dir = Cesium.Cartesian3.subtract(curr, prev, new Cesium.Cartesian3())
      } else {
        const dir1 = Cesium.Cartesian3.subtract(curr, prev, new Cesium.Cartesian3())
        const dir2 = Cesium.Cartesian3.subtract(next, curr, new Cesium.Cartesian3())
        dir = Cesium.Cartesian3.add(dir1, dir2, new Cesium.Cartesian3())
      }
      Cesium.Cartesian3.normalize(dir, dir)

      const up = Cesium.Cartesian3.UNIT_Z
      const rightDir = Cesium.Cartesian3.cross(dir, up, new Cesium.Cartesian3())
      Cesium.Cartesian3.normalize(rightDir, rightDir)
      const leftDir = Cesium.Cartesian3.negate(rightDir, new Cesium.Cartesian3())

      const leftOffset = Cesium.Cartesian3.multiplyByScalar(leftDir, leftDis, new Cesium.Cartesian3())
      const rightOffset = Cesium.Cartesian3.multiplyByScalar(rightDir, rightDis, new Cesium.Cartesian3())

      leftPoints.push(Cesium.Cartesian3.add(curr, leftOffset, new Cesium.Cartesian3()))
      rightPoints.push(Cesium.Cartesian3.add(curr, rightOffset, new Cesium.Cartesian3()))
    }

    const polygon = [...leftPoints, ...rightPoints.reverse()]
    if (polygon.length > 0) polygon.push(polygon[0])
    return polygon
  }

  /**
 * 为带状航线生成虚拟点（每条边的中点）
 */
  private _generateStripVirtualPoints (route: any): void {
    const points = route.centerPoints
    if (points.length < 2) return

    const virtuals: Cesium.Entity[] = []
    for (let i = 0; i < points.length - 1; i++) {
      const start = points[i]
      const end = points[i + 1]
      const mid = new Cesium.Cartesian3(
        (start.x + end.x) / 2,
        (start.y + end.y) / 2,
        (start.z + end.z) / 2
      )
      const virtualPoint = this._dataSource.entities.add({
        position: mid,
        point: {
          pixelSize: 8,
          color: Cesium.Color.WHITE.withAlpha(0.7),
          outlineColor: Cesium.Color.WHITE,
          outlineWidth: 1,
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
        },
        description: `虚拟点 ${i}`,
      }) as any
      virtualPoint.typeInfo = 'strip_virtual_point'
      virtualPoint._edgeIndex = i // 记录边索引，便于插入
      virtuals.push(virtualPoint)
    }
    this._stripVirtualPointsMap.set(route, virtuals)
  }

  /**
 * 清除指定带状航线的虚拟点
 */
  private _clearStripVirtualPoints (route: any): void {
    const virtuals = this._stripVirtualPointsMap.get(route)
    if (virtuals) {
      virtuals.forEach(v => this._dataSource.entities.remove(v))
      this._stripVirtualPointsMap.delete(route)
    }
  }

  /**
 * 在带状航线的指定边后插入新顶点（索引为边索引，插入位置为边的终点之前）
 */
  private _insertStripVertex (route: any, edgeIndex: number, newPosition: Cesium.Cartesian3): void {
    const insertPos = edgeIndex + 1
    route.centerPoints.splice(insertPos, 0, newPosition)
    // 同步添加点实体
    const newPointEntity = this._addPolygonPoint(
      newPosition,
      15,
      new Cesium.Color(1, 1, 1, 1),
      insertPos,
      'polygon_new_edit',
      route.polylineEntity
    )
    route.centerPointEntities.splice(insertPos, 0, newPointEntity)
    // 更新中心线显示
    if (route.polylineEntity) {
      route.polylineEntity.polyline.positions = route.centerPoints.slice()
    }
    // 重新生成带状多边形
    const newPolygon = this._generateStripPolygon(
      route.centerPoints,
      route.leftExpandDis,
      route.rightExpandDis
    )
    if (newPolygon.length > 2) {
      route.polygonEntity.polygon.hierarchy = new Cesium.PolygonHierarchy(newPolygon)
    }
    // 更新边长标签
    this._updateStripDistanceLabels(route)
    // 强制刷新
    // this.viewer.scene.requestRender()
  }

  /**
 * 更新带状航线每段的距离标签（显示在边中点）
 */
  private _updateStripDistanceLabels (route: any): void {
    // 清除旧的 distance 标签
    if (route.textEntityList) {
      route.textEntityList.forEach((t: any) => {
        if (t) {
          this._dataSource.entities.remove(t)
        }
      })
    }
    route.textEntityList = []
    const points = route.centerPoints
    for (let i = 0; i < points.length - 1; i++) {
      const start = points[i]
      const end = points[i + 1]
      const distance = Cesium.Cartesian3.distance(start, end)
      const center = this.calcCenterPosition(start, end)
      const distanceStr = distance > 10000 ? `${(distance / 1000).toFixed(1)}km` : `${distance.toFixed(1)}m`
      const textEntity = this.addText(center, distanceStr)
      textEntity.parent = route.polylineEntity
      route.textEntityList.push(textEntity)
    }
    // 同步更新 stripPointComObj 中的 textEntityList（如果需要外部访问）
    this.stripPointComObj.textEntityList = route.textEntityList
  }

  /**
 * 结束带状航线的拖拽，恢复相机控制
 */
  private _endStripDrag (route: any, dragEntity: any): void {
    if (dragEntity) {
      const controller = this.viewer.scene.screenSpaceCameraController
      controller.enableRotate = true
      controller.enableTranslate = true
      controller.enableZoom = true
      controller.enableTilt = true
      controller.enableLook = true
    }
  }

  /**
 * 启用带状航线的编辑（顶点拖拽 + 虚拟点插入）
 */
  private _enableStripEdit (route: any): void {
    // 清除该航线旧的虚拟点及事件
    this._clearStripVirtualPoints(route)
    if (this._stripEditHandler) {
      this._stripEditHandler.destroy()
    }

    // 生成虚拟点
    this._generateStripVirtualPoints(route)

    this._stripEditHandler = new Cesium.ScreenSpaceEventHandler(this.viewer.scene.canvas)
    let dragEntity: any = null
    let dragIndex: number = -1
    let isVirtual = false

    // 左键按下：选中可拖拽对象（顶点或虚拟点）
    this._stripEditHandler.setInputAction((event: any) => {
      const pick = this.viewer.scene.pick(event.position)
      if (!pick || !pick.id) return

      const entity = pick.id
      // 判断是否为顶点或虚拟点
      if (entity.typeInfo === 'start_point_new' || entity.typeInfo === 'polygon_new_edit') {
        dragEntity = entity
        isVirtual = false
        // 查找该顶点在 centerPoints 中的索引
        dragIndex = route.centerPointEntities.findIndex((e: any) => e.id === entity.id)
      } else if (entity.typeInfo === 'strip_virtual_point') {
        dragEntity = entity
        isVirtual = true
        dragIndex = entity._edgeIndex // 存储所在边的索引
      }

      if (dragEntity) {
        // 禁用相机控制
        const controller = this.viewer.scene.screenSpaceCameraController
        controller.enableRotate = false
        controller.enableTranslate = false
        controller.enableZoom = false
        controller.enableTilt = false
        controller.enableLook = false
      }
    }, Cesium.ScreenSpaceEventType.LEFT_DOWN)

    // 鼠标移动：拖拽更新
    this._stripEditHandler.setInputAction((event: any) => {
      if (!dragEntity || dragIndex === -1) return
      const pickObject = this._getPickObject(event.endPosition, this.viewer)
      const cartesian = pickObject?.position
      if (!cartesian) return
      // 获取高度
      const cartesianPoint = Cesium.Cartographic.fromCartesian(cartesian)
      const longitude = Cesium.Math.toDegrees(cartesianPoint.longitude)
      const latitude = Cesium.Math.toDegrees(cartesianPoint.latitude)
      const height = cartesianPoint.height
      const waypoint = { longitude: longitude, latitude: latitude, height }
      const newPosition = Cesium.Cartesian3.fromDegrees(
        waypoint.longitude,
        waypoint.latitude,
        waypoint.height
      )
      if (isVirtual) {
        // 虚拟点拖拽 ⇒ 插入新顶点
        this._insertStripVertex(route, dragIndex, newPosition)
        // 插入后重新生成虚拟点（旧的全部清除，包括被拖拽的那个）
        this._clearStripVirtualPoints(route)
        this._generateStripVirtualPoints(route)
        // 插入操作完成后立即结束本次拖拽，避免继续移动已销毁的实体
        this._endStripDrag(route, dragEntity)
        dragEntity = null
        dragIndex = -1
        isVirtual = false
      } else {
        // 普通顶点拖拽
        route.centerPoints[dragIndex] = newPosition
        dragEntity.position = newPosition
        // 同步 stripPointComObj 中的位置（如果有需要）
        if (this.stripPointComObj.positionList[dragIndex]) {
          this.stripPointComObj.positionList[dragIndex] = newPosition
        }
        // 更新中心线显示
        if (route.polylineEntity) {
          route.polylineEntity.polyline.positions = route.centerPoints.slice()
        }
        // 重新生成带状多边形
        const newPolygon = this._generateStripPolygon(
          route.centerPoints,
          route.leftExpandDis,
          route.rightExpandDis
        )
        if (newPolygon.length > 2) {
          route.polygonPositions = newPolygon
          route.polygonEntity.polygon.hierarchy = new Cesium.PolygonHierarchy(newPolygon)
          route.polygonEntity.polyline.positions = newPolygon
        }
        // 更新距离标签
        this._updateStripDistanceLabels(route)
        this._clearStripVirtualPoints(route)
        this._generateStripVirtualPoints(route)
        // 强制刷新场景
        this.viewer.scene.requestRender()
      }
    }, Cesium.ScreenSpaceEventType.MOUSE_MOVE)

    // 鼠标释放：结束拖拽
    this._stripEditHandler.setInputAction(() => {
      this._endStripDrag(route, dragEntity)
      dragEntity = null
      dragIndex = -1
      isVirtual = false
      emitter.emit('updateStripPoint', { polygonPositions: route.polygonPositions, polylineList: route.centerPoints })
    }, Cesium.ScreenSpaceEventType.LEFT_UP)
  }

  /**
   * 通过ID获取多边形航线
   */
  getPolygonRouteById (id: string): PolygonRouteEntity | undefined {
    return this._polygonRoutes.find((p) => p.id === id)
  }

  /**
   * 删除多边形航线
   */
  deletePolygonRoute (id: string): boolean {
    const index = this._polygonRoutes.findIndex((p) => p.id === id)
    if (index !== -1) {
      const polygonRoute = this._polygonRoutes[index]

      // 从数据源中删除实体
      const entity = this._dataSource.entities.getById(polygonRoute.entityId)
      if (entity) {
        this._dataSource.entities.remove(entity)
      }

      // 从数组中删除
      this._polygonRoutes.splice(index, 1)
      return true
    }
    return false
  }

  /**
   * 清除所有多边形航线
   */
  clearAllPolygonRoutes (): void {
    this.stopPolygonDrawing()
  }

  /**
   * 停止面状航线绘制
   */
  stopPolygonDrawing (): void {
    this._isDrawingPolygon = false
    this.clearPolygonDrawing()
    this._removePolygonEvents()
  }

  /**
   * 检查是否正在绘制多边形
   */
  isDrawingPolygon (): boolean {
    return this._isDrawingPolygon
  }

  /**
   * 添加起始点
   * @param position 位置信息
   */
  private async addStartingPoint (position: Cesium.Cartesian3): Promise<void> {
    const cartesianPoint = Cesium.Cartographic.fromCartesian(position)
    cartesianPoint.height =
      cartesianPoint.height +
      400 +
      Number(currentSettingData.takeOffSecurityHeight)
    const settingPoint = Cesium.Cartographic.fromCartesian(position)
    settingPoint.height += Number(currentSettingData.takeOffSecurityHeight)
    const positions = Cesium.Cartographic.fromCartesian(position)
    const longitude = Cesium.Math.toDegrees(positions.longitude)
    const latitude = Cesium.Math.toDegrees(positions.latitude)
    let height = 0
    if (currentSettingData.heightMode === WaylineMapType.EGM96) {
      height = Number(currentSettingData.globalHeight)
    } else {
      if (
        currentSettingData.heightMode ===
        WaylineMapType.RELATIVE_TO_START_POINT ||
        currentSettingData.heightMode === WaylineMapType.ABOVE_GROUND_LEVEL
      ) {
        height = Number(currentSettingData.globalHeight) + positions.height
      }
    }
    const pointCartesian = Cesium.Cartesian3.fromDegrees(
      longitude,
      latitude,
      height
    )
    // 创建起始点实体
    const startingPointEntity = this._dataSource.entities.add({
      id: 'route_start_point_billboard',
      position: position,
      billboard: {
        image: '/src/assets/icons/startPoint.png',
        scale: 0.8,
        pixelOffset: new Cesium.Cartesian2(0, 25),
        horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
        verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
        alignedAxis: Cesium.Cartesian3.ZERO,
        disableDepthTestDistance: 5e5,
        distanceDisplayCondition: new Cesium.DistanceDisplayCondition(0, 5e5),
      },
      show: true,
    }) as any
    // 标记为可拖拽航点
    startingPointEntity.isDraggableWaypoint = true
    startingPointEntity.isStartingPoint = true
    startingPointEntity.waypointRefId = `starting_point_${Date.now()}`

    const entityPoint = this._setEntity(
      this._dataSource,
      'aircraft_position_dashLine',
      [position, pointCartesian]
    )
    this._hasStartingPoint = true
    this._startingPoint = position
    // 存储起始点信息
    const waypointInfo: WaypointInfo = {
      longitude,
      latitude,
      height: height,
      id: `starting_point_${Date.now()}`,
      isStartingPoint: true,
    }

    // 添加到航线列表
    const route: RouteEntity = {
      id: waypointInfo.id,
      entityId: startingPointEntity.id,
      type: 'StartingPoint',
      entity: startingPointEntity,
      point: entityPoint,
      position: position,
      positions: [position],
      waypoints: [waypointInfo],
    }

    this.routes.unshift(route)
    if (this.routes.length > 1) {
      this.addRoute(true)
    }
    if (this.store) {
      this.store.commit('SET_START_POINT_POSITION', route.position)
      this.store.commit('SET_START_POINT_FLAG', false)
    }
  }

  /**
   * 在Cesium场景中添加一条带方向的路径线实体
   *
   * @param viewer - Cesium的Viewer实例
   * @param id - 实体的唯一标识符
   * @param positions - 路径点的坐标数组
   * @returns 返回创建的实体对象
   */
  private _setEntity (
    viewer: Cesium.Viewer | Cesium.DataSource,
    id: string,
    positions: Cesium.Cartesian3[]
  ) {
    return viewer.entities.add({
      id: id,
      polyline: {
        positions: positions,
        width: 3,
        clampToGround: false,
        material: new PolylineDirectionMaterial(),
        depthFailMaterial: new Cesium.PolylineDashMaterialProperty({
          color: new Cesium.Color(0, 0.788235294117647, 0.5333333333333333, 1),
          gapColor: new Cesium.Color(0, 0, 0, 0),
          dashLength: 8,
        }),
        zIndex: 1,
      },
      show: false,
    })
  }

  private _setRouteEntity (
    viewer: Cesium.Viewer | Cesium.DataSource,
    id: string,
    positions: Cartesian3[],
    width: number
  ) {
    return viewer.entities.add({
      id: id,
      position: positions[0],
      show: true,
      polyline: {
        positions: positions,
        width: width,
        clampToGround: false,
        material: new Cesium.PolylineDashMaterialProperty({
          color: new Cesium.Color(1, 1, 1, 1),
          gapColor: new Cesium.Color(0, 0, 0, 0),
          dashLength: 10,
        }),
        depthFailMaterial: new Cesium.PolylineDashMaterialProperty({
          color: new Cesium.Color(1, 1, 1, 1),
          gapColor: new Cesium.Color(0, 0, 0, 0),
          dashLength: 10,
        }),
      },
      point: {
        pixelSize: 6,
        color: new Cesium.Color(1, 1, 1, 1),
        disableDepthTestDistance: 3e6,
        show: true,
      },
    })
  }

  /**
   * 使用变换矩阵计算完整欧拉角（包含翻滚角）
   * @param origin 原点坐标
   * @param target 目标点坐标
   * @param upDirection 上方向向量，默认为Z轴
   * @returns 完整欧拉角
   */
  calculateCompleteEulerAnglesWithCesium (
    origin: Cesium.Cartesian3,
    target: Cesium.Cartesian3
  ) {
    const point1Horizontal = Cesium.Transforms.eastNorthUpToFixedFrame(origin)
    const point2Horizontal = Cesium.Matrix4.inverse(
      point1Horizontal,
      new Cesium.Matrix4()
    )
    const cartesian = Cesium.Matrix4.multiplyByPoint(
      point2Horizontal,
      target,
      new Cesium.Cartesian3()
    )
    const heading = Math.atan2(cartesian.x, cartesian.y)
    const distance = Cesium.Cartesian3.magnitude(cartesian)
    const pitch = Math.asin(cartesian.z / distance)
    return {
      heading: heading,
      distance: distance,
      pitch: pitch,
      roll: 0,
    }
  }

  /**
   * 从四元数获取欧拉角
   * @param quaternion 四元数
   * @returns 欧拉角
   */
  eulerFromQuaternion (quaternion: Cesium.Quaternion) {
    const matrix = Cesium.Matrix3.fromQuaternion(quaternion)
    const angles = {
      heading: 0,
      pitch: 0,
      roll: 0,
    }

    // 从旋转矩阵提取欧拉角
    angles.heading = Math.atan2(matrix[1], matrix[0])
    angles.pitch = Math.asin(-matrix[2])
    angles.roll = Math.atan2(matrix[5], matrix[8])

    return {
      heading: Cesium.Math.toDegrees(angles.heading),
      pitch: Cesium.Math.toDegrees(angles.pitch),
      roll: Cesium.Math.toDegrees(angles.roll),
    }
  }

  /**
   * 添加航点
   * @param position 位置信息
   * @param isDrawing 是否在绘制中（放大显示）
   */
  async addWaypoint (
    position: any,
    positions: Cesium.Cartesian3[],
    isDrawing: boolean = false,
    fileArr?: any[]
  ): Promise<Cesium.Entity> {
    const { longitude, latitude, height } = position
    let terrainHeight = await getHeight()
    if (fileArr) {
      terrainHeight = height
    }
    const entity = this._addEntity(
      {
        position: Cartesian3.fromDegrees(longitude, latitude, terrainHeight),
        ...this.pointOption(this.waypoints.length + 1, isDrawing),
        // polyline: {
        //   positions: new CallbackProperty(() => {
        //     return [
        //       Cartesian3.fromDegrees(longitude, latitude, 0),
        //       Cartesian3.fromDegrees(longitude, latitude, terrainHeight),
        //     ]
        //   }, false),
        //   material: new StripeMaterialProperty({ ...stripeMaterialPropertyOption }),
        //   width: isDrawing ? 5 : 3,
        // },
      },
      'waypoint'
    )
    const point = this._setRouteEntity(
      this._dataSource,
      `route_point_polylineDash_${this._drawPointLine.length}`,
      positions,
      2
    )
    // 存储航点信息
    const waypointInfo: WaypointInfo = {
      longitude,
      latitude,
      height,
      id: `waypoint_${Date.now()}`,
    }

    this.waypoints.push(waypointInfo)
    const isUseGlobalHeight = this.store?.state.isUseGlobalHeight

    // 添加到航线列表
    let route: RouteEntity = {
      id: waypointInfo.id,
      entityId: entity.id,
      type: 'Waypoint',
      entity: entity,
      point: point,
      eventList: [],
      hpr: {
        heading: 0,
        pitch: 0,
        roll: 0,
      },
      index: this._drawPointLine.length,
      position: Cartesian3.fromDegrees(longitude, latitude, terrainHeight),
      positions: [Cartesian3.fromDegrees(longitude, latitude, terrainHeight)],
      waypoints: [waypointInfo],
      useGlobalTurnParam: 1,
      waypointTurnMode: currentSettingData.waypointTurnMode,
      waypointTurnDampingDist: 0.2,
      useGlobalHeadingParam: 1,
      waypointHeadingParam: {
        waypointHeadingMode: currentSettingData.waypointHeadingMode,
        waypointHeadingAngle: 0,
        waypointHeadingPathMode: FollowBadType.FOLLOW_BAD_ARC,
      },
      useGlobalSpeedParam: 1,
      waypointSpeed: currentSettingData.autoFlightSpeed,
      isUseGlobalHeight: isUseGlobalHeight,
    }
    if (fileArr) {
      route = {
        ...route,
        ...fileArr,
      }
    }

    this.routes.push(route)
    this._drawPointLine.push(route)

    if (this.store) {
      this.store.state.currentRoutePosIndex = this._drawPointLine.length - 1
      this.store.commit(
        'SET_EDIT_ROUTER_LIST',
        this.getSerializableDrawPointLine()
      )
    }
    return entity
  }

  getPointLines (): any {
    return this.getSerializableDrawPointLine()
  }

  /** 更新视椎体信息
   * @param id 视锥体对应的航点ID
   * @param heading 航点的航向角
   * @param pitch 航点的俯仰角
   */
  changeFrustum (id: string, heading: number, pitch: number) {
    const point = this._drawPointLine.find((item) => item.entityId === id)
    const point1 = this.routes.find((item) => item.entityId === id)
    const point2 = this.waypoints.find((item) => item.id === id)
    if (point) {
      point.hpr.heading = heading
      point.hpr.pitch = pitch
      point.hpr.roll = 0
    }
    if (point1) {
      point.hpr.heading = heading
      point.hpr.pitch = pitch
      point.hpr.roll = 0
    }
    if (point2) {
      point.hpr.heading = heading
      point.hpr.pitch = pitch
      point.hpr.roll = 0
    }
  }

  /**
   * 获取指定的视锥体可视化对象
   *
   * @param id 要获取的视锥体可视化对象的ID
   * @returns 返回对应的视锥体可视化对象，如果未找到则返回undefined
   */
  getFrustumVisualizer (id: string) {
    return this._getFrustumByWaypointId(id)
  }

  // 根据变焦倍数调整所有视锥体的FOV
  adjustFrustumsByZoomFactor (zoomFactor: number, waypointId: string) {
    const allFrustum = state.drawRouteTool?.getAllFrustums()
    if (!allFrustum) return
    allFrustum.forEach((visualizer) => {
      // 计算新的FOV，变焦倍数越大，FOV越小
      const baseFov = 60 // 基准FOV
      const newFov = baseFov / zoomFactor
      const clampedFov = Math.max(10, Math.min(120, newFov))
      // 更新视锥体的FOV
      visualizer.updateFov(clampedFov)
    })
    nextTick(() => {
      state.frustumTool?.show(waypointId, true)
    })
  }

  /**
   * 添加航线
   * @param isDrawing 是否在绘制中
   */
  addRoute (isDrawing: boolean = false): void {
    if (this.waypoints.length < 1 || !this._startingPoint) return

    // 创建包含起始点和所有航点的位置数组
    const positions = []
    const startPoints = getSetFlyToFirstPoint(this.viewer, this._startingPoint, this._drawPointLine[0].position, {
      flyToWaylineMode: currentSettingData.flyToWaylineMode,
      takeOffSecurityHeight: currentSettingData.takeOffSecurityHeight,
    })
    positions.push(...startPoints)
    this.routes
      .filter((r) => r.type === 'Waypoint')
      .forEach((route) => {
        positions.push(route.positions[0])
      })

    const polyline = this._addEntity({
      polyline: {
        positions,
        ...polylineOption,
        clampToGround: false, // 禁用贴地，使用绝对高度
      },
    }) as any

    // 标记为绘制中的实体
    if (isDrawing) {
      polyline._isDrawing = true
    }

    const routeEntity: RouteEntity = {
      id: `route_${Date.now()}`,
      type: 'Route',
      entity: polyline,
      positions: positions,
      position: positions[0],
      waypoints: this.waypoints,
    }

    this.routes.push(routeEntity)
    this.selectRoute(routeEntity)

    // 如果是绘制中，缩放以显示所有点
    if (isDrawing) {
      this.viewer.zoomTo(polyline)
    }
  }

  /**
   * 继续绘制点线
   */
  drawDrawPointLine (): void {
    this._removeDrawEvents()
    this.clearSelection()
    this._drawHandler = new Cesium.ScreenSpaceEventHandler(
      this.viewer.scene.canvas
    )
    this._leftClickEventForPointLine()
  }

  removeMovePoint (editPointIndex): void {
    if (!this._drawPointLine[editPointIndex] || !this._drawPointLine[editPointIndex].entity) {
      console.warn('setMovePoint: 无效的航点索引或实体不存在')
      return
    }
    const targetRoute = this._drawPointLine[editPointIndex]
    const targetEntity = targetRoute.entity
    if (targetEntity._dragHandler) {
      targetEntity._dragHandler.removeInputAction(Cesium.ScreenSpaceEventType.LEFT_DOWN)
      targetEntity._dragHandler.removeInputAction(Cesium.ScreenSpaceEventType.MOUSE_MOVE)
      targetEntity._dragHandler.removeInputAction(Cesium.ScreenSpaceEventType.LEFT_UP)
      targetEntity._dragHandler.destroy()
      targetEntity._dragHandler = null
    }
  }

  /**
 * 启用指定航点的拖拽移动功能（仅移动特定航点）
 * @param editPointIndex 要移动的航点在 _drawPointLine 中的索引
 */
  setMovePoint (editPointIndex: number): void {
    if (!this._drawPointLine[editPointIndex]?.entity) {
      console.warn('setMovePoint: 无效的航点索引')
      return
    }
    this._editPointIndex = editPointIndex
    this._originalPositionsList = cloneDeep(this._drawPointLine)
    const targetRoute = this._drawPointLine[this._editPointIndex]
    const targetEntity = targetRoute.entity
    let altTargetEvent = null
    let altHeight = 0
    // 获取当前的航线折线实体（用于实时更新）
    let routeEntity = this.routes.find(r => r.type === 'Route' && (r.entity as any)._isDrawing)
    if (!routeEntity) {
      routeEntity = this.routes.find(r => r.type === 'Route')
    }
    const polylineEntity = routeEntity?.entity

    // 辅助函数：根据当前 _drawPointLine 和起始点重新计算折线 positions
    const rebuildPolylinePositions = () => {
      if (!polylineEntity) return
      const positions: Cartesian3[] = []
      // 加入起始段（可能包含插值点）
      if (this._hasStartingPoint && this._drawPointLine.length > 0) {
        const startPoints = getSetFlyToFirstPoint(
          this.viewer,
          this._startingPoint,
          this._drawPointLine[0].position,
          {
            flyToWaylineMode: currentSettingData.flyToWaylineMode,
            takeOffSecurityHeight: currentSettingData.takeOffSecurityHeight,
          }
        )
        positions.push(...startPoints)
      }
      // 加入所有航点
      this._drawPointLine.forEach(route => {
        positions.push(route.position)
      })
      polylineEntity.polyline.positions = positions
    }

    // 移除旧的事件处理器
    if (targetEntity._dragHandler) {
      targetEntity._dragHandler.destroy()
      targetEntity._dragHandler = null
    }

    const dragHandler = new Cesium.ScreenSpaceEventHandler(this.viewer.scene.canvas)
    targetEntity._dragHandler = dragHandler

    let isDragging = false

    // 左键按下
    dragHandler.setInputAction((event: any) => {
      if (isMoveAlt.value) return
      const pickObject = this._getPickObject(event.position, this.viewer)
      const pickedEntity = pickObject?.pickedObject?.id
      if (pickedEntity !== targetEntity) return

      isDragging = true
      // 禁用相机控制
      const controller = this.viewer.scene.screenSpaceCameraController
      controller.enableRotate = false
      controller.enableTranslate = false
      controller.enableZoom = false
      controller.enableTilt = false
      controller.enableLook = false

      if (isMoveAlt.value) {
        this.viewer.canvas.style.cursor = 'n-resize'
      } else {
        this.viewer.canvas.style.cursor = 'move'
      }
      this._originalPositions = [Cesium.Cartesian3.clone(targetRoute.position)]
    }, Cesium.ScreenSpaceEventType.LEFT_DOWN)

    // 鼠标移动
    dragHandler.setInputAction((event: any) => {
      if (isMoveAlt.value) return
      const pickObject = this._getPickObject(event.endPosition, this.viewer)
      const pickedEntity = pickObject?.pickedObject?.id
      if (pickedEntity === targetEntity) {
        this.viewer.canvas.style.cursor = 'move'
        isMove.value = true
        moveStyle.value.left = `${event.endPosition.x + 30}px`
        moveStyle.value.top = `${event.endPosition.y + 30}px`
      } else {
        this.viewer.canvas.style.cursor = 'default'
      }
      if (!isDragging) return

      const cartesianPoint = Cesium.Cartographic.fromCartesian(pickObject.position)
      const cartesianLongitude = Cesium.Math.toDegrees(cartesianPoint.longitude)
      const cartesianLatitude = Cesium.Math.toDegrees(cartesianPoint.latitude)
      const cartesianHeight = cartesianPoint.height
      // 更新当前航点位置
      const newPosition = Cesium.Cartesian3.fromDegrees(cartesianLongitude, cartesianLatitude, cartesianHeight)
      if (!newPosition) return
      targetRoute.isUseGlobalHeight = false
      targetRoute.position = newPosition
      targetEntity.position = newPosition
      // 同步 waypoints 数据
      if (targetRoute.waypoints[0]) {
        targetRoute.waypoints[0].longitude = cartesianLongitude
        targetRoute.waypoints[0].latitude = cartesianLatitude
        targetRoute.waypoints[0].height = cartesianHeight
        // 同步 this.waypoints
        const wp = this.waypoints.find(w => w.id === targetRoute.waypoints[0].id)
        if (wp) {
          wp.longitude = cartesianLongitude
          wp.latitude = cartesianLatitude
          wp.height = cartesianHeight
        }
      }

      // 更新垂线实体（点到地面）
      const endPoints = this.getPoints(newPosition, this.viewer)
      if (targetRoute.point) {
        targetRoute.point.position = newPosition
        targetRoute.point.polyline.positions = [newPosition, endPoints]
      }

      // 同步 this.routes 中的对应实体
      const ent = this.routes.find(r => r.entity.id === targetEntity.id)
      if (ent) {
        ent.position = newPosition
        ent.positions = [newPosition]
        ent.waypoints = targetRoute.waypoints
        ent.point.position = newPosition
        ent.point.polyline.positions = [newPosition, endPoints]
      }

      // 实时更新航线折线
      rebuildPolylinePositions()

      this.viewer.scene.requestRender()
    }, Cesium.ScreenSpaceEventType.MOUSE_MOVE)
    // 左键释放
    dragHandler.setInputAction(() => {
      if (isMoveAlt.value) return
      if (isDragging) {
        isDragging = false
        // 恢复相机控制
        const controller = this.viewer.scene.screenSpaceCameraController
        controller.enableRotate = true
        controller.enableTranslate = true
        controller.enableZoom = true
        controller.enableTilt = true
        controller.enableLook = true
        this.viewer.canvas.style.cursor = 'default'
        isMove.value = false
        isMoveAlt.value = false
        // 最后再更新一次折线，确保最终状态
        rebuildPolylinePositions()
        // 重新计算受影响的航向角
        this._recalculateHeadingsAround(this._editPointIndex)
        // 保存到 store
        if (this.store) {
          this.store.commit('SET_EDIT_ROUTER_LIST', this.getSerializableDrawPointLine())
          this.store.commit('SET_EDIT_CURRENT_POINT', this.serializeRouteEntity(targetRoute))
        }
        isSave.value = true
      }
    }, Cesium.ScreenSpaceEventType.LEFT_UP)
    dragHandler.setInputAction((event: any) => {
      const pickObject = this._getPickObject(event.position, this.viewer)
      const pickedEntity = pickObject?.pickedObject?.id
      if (pickedEntity !== targetEntity) return

      isDragging = true
      // 禁用相机控制
      const controller = this.viewer.scene.screenSpaceCameraController
      controller.enableRotate = false
      controller.enableTranslate = false
      controller.enableZoom = false
      controller.enableTilt = false
      controller.enableLook = false
      isMoveAlt.value = true
      this.viewer.canvas.style.cursor = 'n-resize'
      altTargetEvent = event.position
      altHeight = targetRoute.waypoints[0].height * 1
      this._originalPositions = [Cesium.Cartesian3.clone(targetRoute.position)]
    }, Cesium.ScreenSpaceEventType.LEFT_DOWN, Cesium.KeyboardEventModifier.ALT)
    dragHandler.setInputAction((event: any) => {
      const pickObject = this._getPickObject(event.endPosition, this.viewer)
      const pickedEntity = pickObject?.pickedObject?.id
      if (pickedEntity === targetEntity) {
        this.viewer.canvas.style.cursor = 'n-resize'
        isMove.value = true
        isMoveAlt.value = true
        moveStyle.value.left = `${event.endPosition.x + 30}px`
        moveStyle.value.top = `${event.endPosition.y + 30}px`
      }
      if (!isDragging) return

      const cartesianLongitude = targetRoute.waypoints[0].longitude * 1
      const cartesianLatitude = targetRoute.waypoints[0].latitude * 1
      let cartesianHeight = targetRoute.waypoints[0].height
      if (altTargetEvent !== null) {
        const obj = {
          x: altTargetEvent.x - event.endPosition.x,
          y: altTargetEvent.y - event.endPosition.y,
        }
        /** obj.y 计算1/5 便于精确鼠标移动 */
        cartesianHeight = altHeight + (obj.y / 5)
      }

      const newPosition = Cesium.Cartesian3.fromDegrees(cartesianLongitude, cartesianLatitude, cartesianHeight)
      if (!newPosition) return
      console.log(cartesianHeight, 'cartesianHeight', targetRoute.waypoints[0].height)
      targetRoute.isUseGlobalHeight = false
      targetRoute.position = newPosition
      targetEntity.position = newPosition

      // 同步 waypoints 数据
      if (targetRoute.waypoints[0]) {
        targetRoute.waypoints[0].longitude = cartesianLongitude
        targetRoute.waypoints[0].latitude = cartesianLatitude
        targetRoute.waypoints[0].height = cartesianHeight
        // 同步 this.waypoints
        const wp = this.waypoints.find(w => w.id === targetRoute.waypoints[0].id)
        if (wp) {
          wp.longitude = cartesianLongitude
          wp.latitude = cartesianLatitude
          wp.height = cartesianHeight
        }
      }

      // 更新垂线实体（点到地面）
      const endPoints = this.getPoints(newPosition, this.viewer)
      if (targetRoute.point) {
        targetRoute.point.position = newPosition
        targetRoute.point.polyline.positions = [newPosition, endPoints]
      }

      // 同步 this.routes 中的对应实体
      const ent = this.routes.find(r => r.entity.id === targetEntity.id)
      if (ent) {
        ent.position = newPosition
        ent.positions = [newPosition]
        ent.waypoints = targetRoute.waypoints
        ent.point.position = newPosition
        ent.point.polyline.positions = [newPosition, endPoints]
      }

      // 实时更新航线折线
      rebuildPolylinePositions()
      emitter.emit('changeEditHeight', { height: cartesianHeight })
    }, Cesium.ScreenSpaceEventType.MOUSE_MOVE, Cesium.KeyboardEventModifier.ALT)
    dragHandler.setInputAction(() => {
      if (isDragging) {
        isDragging = false
        // 恢复相机控制
        const controller = this.viewer.scene.screenSpaceCameraController
        controller.enableRotate = true
        controller.enableTranslate = true
        controller.enableZoom = true
        controller.enableTilt = true
        controller.enableLook = true
        this.viewer.canvas.style.cursor = 'default'
        isMove.value = false
        isMoveAlt.value = false
        altTargetEvent = null
        altHeight = 0
        // 最后再更新一次折线，确保最终状态
        rebuildPolylinePositions()
        // 重新计算受影响的航向角
        this._recalculateHeadingsAround(this._editPointIndex)
        const entity = this._drawPointLine[this._editPointIndex].entity
        this.showFrustum(this._drawPointLine[this._editPointIndex].position, entity)
        // 保存到 store
        if (this.store) {
          this.store.commit('SET_EDIT_ROUTER_LIST', this.getSerializableDrawPointLine())
          this.store.commit('SET_EDIT_CURRENT_POINT', this.serializeRouteEntity(targetRoute))
        }
        isSave.value = true
      }
    }, Cesium.ScreenSpaceEventType.LEFT_UP, Cesium.KeyboardEventModifier.ALT)
  }

  /**
 * 重新计算受影响航点的航向角（当前点、前一点、后一点）
 * @param index 拖拽的航点索引
 * @private
 */
  private _recalculateHeadingsAround (index: number): void {
    // 重新计算前一个航点到当前航点的方向（当前点的 heading）
    if (index === this._drawPointLine.length - 1) {
      const prevRoute = this._drawPointLine[index - 1]
      const currentRoute = this._drawPointLine[index]
      if (prevRoute && currentRoute) {
        const origin = prevRoute.waypoints[0]
        const target = currentRoute.waypoints[0]
        const startPointInfo = getEllipsoidCartesian({
          lng: origin.longitude,
          lat: origin.latitude,
          h: origin.height,
        })
        const endPointInfo = getEllipsoidCartesian({
          lng: target.longitude,
          lat: target.latitude,
          h: target.height,
        })
        const positionOption = getPointPosition(startPointInfo, endPointInfo)
        currentRoute.hpr.heading = Math.floor(Cesium.Math.toDegrees(positionOption.h) + 90)
      }
    } else {
      const prevRoute = this._drawPointLine[index + 1]
      const currRoute = this._drawPointLine[index]
      if (prevRoute && currRoute) {
        const origin = prevRoute.position
        const target = currRoute.position
        const startPoint = this.conversion(origin)
        const endPoint = this.conversion(target)
        const startPointInfo = getEllipsoidCartesian({
          lng: startPoint.longitude,
          lat: startPoint.latitude,
          h: startPoint.height
        })
        const endPointInfo = getEllipsoidCartesian({
          lng: endPoint.longitude,
          lat: endPoint.latitude,
          h: endPoint.height
        })
        const positionOption = getPointPosition(endPointInfo, startPointInfo)
        const heading = Math.floor(
          Cesium.Math.toDegrees(positionOption.h) + 90,
        )
        this._drawPointLine[index].hpr.heading = heading
        console.log('_recalculateHeadingsAround', heading, 'heading')
      }
    }

    // 重新计算当前航点到下一个航点的方向（下一个点的 heading）
    const nextIndex = index + 1
    const nextRoute = this._drawPointLine[nextIndex]
    if (nextRoute) {
      if (nextIndex === this._drawPointLine.length - 1) {
        const prevRoute = this._drawPointLine[nextIndex - 1]
        const currentRoute = this._drawPointLine[nextIndex]
        if (prevRoute && currentRoute) {
          const origin = prevRoute.waypoints[0]
          const target = currentRoute.waypoints[0]
          const startPointInfo = getEllipsoidCartesian({
            lng: origin.longitude,
            lat: origin.latitude,
            h: origin.height,
          })
          const endPointInfo = getEllipsoidCartesian({
            lng: target.longitude,
            lat: target.latitude,
            h: target.height,
          })
          const positionOption = getPointPosition(startPointInfo, endPointInfo)
          currentRoute.hpr.heading = Math.floor(Cesium.Math.toDegrees(positionOption.h) + 90)
          console.log('_recalculateHeadingsAround', currentRoute.hpr.heading, 'heading')
        }
      } else {
        const prevRoute = this._drawPointLine[nextIndex + 1]
        const currRoute = this._drawPointLine[nextIndex]
        if (prevRoute && currRoute) {
          const origin = prevRoute.position
          const target = currRoute.position
          const startPoint = this.conversion(origin)
          const endPoint = this.conversion(target)
          const startPointInfo = getEllipsoidCartesian({
            lng: startPoint.longitude,
            lat: startPoint.latitude,
            h: startPoint.height
          })
          const endPointInfo = getEllipsoidCartesian({
            lng: endPoint.longitude,
            lat: endPoint.latitude,
            h: endPoint.height
          })
          const positionOption = getPointPosition(endPointInfo, startPointInfo)
          const heading = Math.floor(
            Cesium.Math.toDegrees(positionOption.h) + 90,
          )
          this._drawPointLine[nextIndex].hpr.heading = heading
          console.log('_recalculateHeadingsAround', heading, 'heading')
        }
      }
    }
  }

  /** 飞到此处 */
  flyPointLine (point): void {
    const entity = this._drawPointLine[this.store.state.currentRoutePosIndex]
    const positions = Cesium.Cartesian3.fromDegrees(point.longitude, point.latitude, point.height)
    const heading = entity ? entity.hpr.heading - 90 : this._frustumTool.heading - 90
    const pitch = entity ? entity.hpr.pitch : this._frustumTool.pitch
    this._frustumTool.update(positions, heading, pitch)
  }

  /**
 * 在指定位置插入航点并整体重建航线实体
 *
 * @param insertIndex  插入位置索引（基于 _drawPointLine 数组）
 * @param position     新航点的笛卡尔坐标
 * @param hpr          航向/俯仰/翻滚角对象
 * @param isUseGlobalHeight 是否使用全局高度（传入时已转换为布尔标志）
 * @param cb            插入完成后的回调函数（可选）
 */
  insertWaypointAndRebuild (
    insertIndex: number,
    position: Cesium.Cartesian3,
    hpr: { heading: number; pitch: number; roll: number },
    isUseGlobalHeight: boolean,
    cb?: Function
  ): void {
    const dataSource: Cesium.CustomDataSource = this._dataSource
    const index = insertIndex < 0 ? 0 : insertIndex > this._drawPointLine.length ? this._drawPointLine.length : insertIndex
    // 1. 构造与现有 addInWaypoint 相同结构的航点条目
    const newWaypointEntry: any = {
      position,
      useGlobalHeight: isUseGlobalHeight ? 1 : 0,
      eventList: [],
      isWarning: false,
      hpr,
      useGlobalTurnParam: 1,
      waypointTurnMode: currentSettingData.waypointTurnMode,
      waypointTurnDampingDist: 1,
      useGlobalHeadingParam: 1,
      waypointHeadingParam: {
        waypointHeadingMode: currentSettingData.waypointHeadingMode,
        waypointHeadingAngle: 0,
        waypointHeadingPathMode: FollowBadType.FOLLOW_BAD_ARC,
      },
      useGlobalSpeedParam: 1,
      waypointSpeed: currentSettingData.autoFlightSpeed,
    }

    // 2. 将新点插入 _drawPointLine
    this._drawPointLine.splice(index, 0, newWaypointEntry)

    // 3. 保存当前完整列表（深拷贝引用即可，因为之后会被清空）
    const pointList = this._drawPointLine.map(el => el)
    const startPoint = this._startingPoint
    const startPosition = this.conversion(startPoint)
    pointList.forEach((item, i) => {
      if (index === pointList.length - 1) {
        const start = this.conversion(pointList[i - 1].position)
        const point = this.conversion(item.position)
        const startPointInfo = getEllipsoidCartesian({
          lng: start.longitude,
          lat: start.latitude,
          h: start.height,
        })
        const endPointInfo = getEllipsoidCartesian({
          lng: point.longitude,
          lat: point.latitude,
          h: point.height,
        })
        const positionOption = getPointPosition(startPointInfo, endPointInfo)
        item.hpr.heading = Math.floor(
          Cesium.Math.toDegrees(positionOption.h) + 90,
        )
      } else {
        const start = this.conversion(pointList[i + 1].position)
        const point = this.conversion(item.position)
        const startPointInfo = getEllipsoidCartesian({
          lng: start.longitude,
          lat: start.latitude,
          h: start.height,
        })
        const endPointInfo = getEllipsoidCartesian({
          lng: point.longitude,
          lat: point.latitude,
          h: point.height,
        })
        const positionOption = getPointPosition(endPointInfo, startPointInfo)
        item.hpr.heading = Math.floor(
          Cesium.Math.toDegrees(positionOption.h) + 90,
        )
      }
    })
    // 4. 清空所有可重建的实体
    this.clearAllPointLine()
    this._frustumTool?.hide()
    this._removeAllFrustums()
    // 5. 使用 importRouteFromFile 逐点重建整个航线
    this.importRouteFromFile(pointList, index, () => {
      this.store.state.currentRoutePosIndex = index
      this.store.commit('SET_EDIT_CURRENT_POINT', pointList[index])
      if (cb) {
        cb()
      }
    })
  }

  /** 更新航线（通用入口） */
  async updateRouteFrom (list: any[]): Promise<void> {
    // 4. 清空所有可重建的实体
    this.clearAllPointLine()
    this._frustumTool?.hide()
    this._removeAllFrustums()
    // 5. 使用 importRouteFromFile 逐点重建整个航线
    this.importRouteFromFile(list, this.store.state.currentRoutePosIndex, () => {
      this.store.commit('SET_EDIT_CURRENT_POINT', this._drawPointLine[this.store.state.currentRoutePosIndex])
    })
  }

  /** 更新航线（编辑模式，与 updateRouteFrom 相同行为，可保留以兼容原有调用） */
  async updateRouteFromEdit (list: any[]): Promise<void> {
    // 4. 清空所有可重建的实体
    this.clearAllPointLine()
    this._frustumTool?.hide()
    this._removeAllFrustums()
    // 5. 使用 importRouteFromFile 逐点重建整个航线
    this.importRouteFromFile(list, this.store.state.currentRoutePosIndex, () => {
      this.store.commit('SET_EDIT_CURRENT_POINT', this._drawPointLine[this.store.state.currentRoutePosIndex])
    })
  }

  /**
   * 从文件导入航线
   * @param list 航点列表
   */
  async setRouteStartPoint (item) {
    await this.addStartingPoint(item)
  }

  /** 删除航点 */
  deletePoint (pos): void {
    this._dataSource.entities.remove(pos)
    let pointIndex = null
    let entityIndex = null
    this.waypoints.forEach((el, index) => {
      if (el.id === pos.id) {
        this.waypoints.splice(index, 1)
      }
    })
    this._drawPointLine.forEach((item, i) => {
      if (item.entity.id === pos.id) {
        pointIndex = i
      }
    })
    this.routes.forEach((el, i) => {
      if (el.entity.id === pos.id) {
        entityIndex = i
        this._dataSource.entities.remove(el.entity)
        this._dataSource.entities.remove(el.point)
      }
    })
    if (pointIndex !== null) {
      this._drawPointLine.splice(pointIndex, 1)
      if (this._drawPointLine.length > 0) {
        this.setFrustum(
          this._drawPointLine[this._drawPointLine.length - 1].entity.id
        )
      }
      this.store.commit(
        'SET_EDIT_ROUTER_LIST',
        this.getSerializableDrawPointLine()
      )
    }
    if (entityIndex !== null) {
      this.routes.splice(entityIndex, 1)
    }

    if (this._hasStartingPoint && this.waypoints.length >= 1) {
      const oldRoute = this.routes.find((r) => r.type === 'Route')
      if (oldRoute) {
        this.deleteRoute(oldRoute.id)
      }
      this.addRoute(true)
    }
  }

  deletePointLine (entitys: RouteEntity): void {
    const entity = entitys.positions[0]
    let pointIndex = null
    let entityIndex = null
    this.waypoints.forEach((el, index) => {
      if (el.id === entitys.id) {
        this.waypoints.splice(index, 1)
      }
    })
    this._drawPointLine.forEach((item, i) => {
      if (item.entity.id === entitys.entityId) {
        pointIndex = i
      }
    })
    this.routes.forEach((el, i) => {
      if (el.entity.id === entitys.entityId) {
        entityIndex = i
        this._dataSource.entities.remove(el.entity)
        this._dataSource.entities.remove(el.point)
      }
    })
    if (pointIndex !== null) {
      this._drawPointLine.splice(pointIndex, 1)
      if (this._drawPointLine.length > 0) {
        this.setFrustum(
          this._drawPointLine[this._drawPointLine.length - 1].entity.id
        )
      }
      this.store.commit(
        'SET_EDIT_ROUTER_LIST',
        this.getSerializableDrawPointLine()
      )
    }
    if (entityIndex !== null) {
      this.routes.splice(entityIndex, 1)
    }

    if (this._hasStartingPoint && this.waypoints.length >= 1) {
      const oldRoute = this.routes.find((r) => r.type === 'Route')
      if (oldRoute) {
        this.deleteRoute(oldRoute.id)
      }
      this.addRoute(true)
    }
  }

  importRouteFormPolygonFile (list: any[]): void {
    this._removeDrawEvents()
    this.clearSelection()
    this.clearAllPointLine()
    if (!this.polygonComObj.polygonEntity) {
      this.initPolygonEntity()
    }
    list.forEach(async (item: any, index) => {
      const cartesian = item
      if (!cartesian) return

      const cartesianPoint = Cesium.Cartographic.fromCartesian(cartesian)
      const longitude = Cesium.Math.toDegrees(cartesianPoint.longitude)
      const latitude = Cesium.Math.toDegrees(cartesianPoint.latitude)
      const height = cartesianPoint.height
      const position = { longitude: longitude, latitude: latitude, height }
      this.polygonComObj.positionList.push(cartesian)
      this.polygonComObj.copyPositionList.push(
        JSON.parse(JSON.stringify(cartesian))
      )
      this.polygonComObj.movePositions = [...this.polygonComObj.positionList]

      // 添加视觉点
      const point = this._addPolygonPoint(
        cartesian,
        15,
        new Cesium.Color(1, 1, 1, 1),
        this.polygonComObj.positionList.length - 1,
        'polygon_new',
        this.polygonComObj.polygonEntity!
      ) as PointEntity
      this.polygonComObj.pointEntityList.push(point)

      if (this.polygonComObj.pointEntityList.length > 2) {
        this.polygonComObj.pointEntityList[
          this.polygonComObj.pointEntityList.length - 1
        ].point!.show = true
        this.polygonComObj.pointEntityList[
          this.polygonComObj.pointEntityList.length - 1
        ].billboard!.show = true
        this.polygonComObj.pointEntityList[
          this.polygonComObj.pointEntityList.length - 2
        ].point!.show = true
        this.polygonComObj.pointEntityList[
          this.polygonComObj.pointEntityList.length - 2
        ].billboard!.show = false
      }

      this.polygonComObj.polygonEntity!.polygon!.hierarchy = [
        ...this.polygonComObj.positionList,
      ]

      if (this.polygonComObj.positionList.length > 1) {
        this.polygonComObj.polygonEntity!.polyline!.positions = [
          ...this.polygonComObj.positionList,
          this.polygonComObj.positionList[0],
        ]
      }
    })
  }

  setPolygonPointLine (list: any[], i = 0): void {
    this._removeDrawEvents()
    this.clearSelection()
    this.clearAllPointLine()
    const positions = [] as Cesium.Cartesian3[]
    list[i].geographicPoints.forEach(async (item: any, index) => {
      const cartesian = Cesium.Cartesian3.fromDegrees(
        item[0],
        item[1]
      )
      positions.push(cartesian)
    })
    if (this._hasStartingPoint) {
      positions.unshift(this._startingPoint)
    }
    if (!this.polygonComObj.polylineEntity) {
      this.polygonComObj.polylineEntity = this._setEntity(
        this._dataSource,
        'route_polygon_polylineDash',
        positions
      ) as any
    } else {
      this._dataSource.entities.remove(this.polygonComObj.polylineEntity)
      this.polygonComObj.polylineEntity = this._setEntity(
        this._dataSource,
        'route_polygon_polylineDash',
        positions
      ) as any
    }
    this.polygonComObj.polylineEntity!.show = true
    this.viewer.flyTo(this.polygonComObj.polylineEntity)
  }

  /** 隐藏航线以及点以及拖拽行为 */
  hideRoute () {
    this.routes.forEach(route => {
      const entity = route.entity as any
      const point = route.point as any
      entity.show = false
      if (point) { point.show = false }
    })
    this._frustumMap.forEach((frustum, waypointId) => {
      frustum.hide()
    })
    this._frustumTool?.hide()
  }

  showRoute () {
    this.routes.forEach(route => {
      const entity = route.entity as any
      const point = route.point as any
      entity.show = true
      if (point) { point.show = true }
    })
    this._frustumTool?.show()
  }

  /**
   * 从文件导入航线
   * @param list 航点列表
   * @param i 起始索引
   * @param cb 回调函数
   */
  importRouteFromFile (list: any[], i: number | null = null, cb?: Function): void {
    this._removeDrawEvents()
    this.clearSelection()
    this.clearAllPointLine()

    list.forEach(async (item: any, index) => {
      let cartesian = item.position
      if (!cartesian) return

      const cartesianPoint = Cesium.Cartographic.fromCartesian(cartesian)
      const longitude = Cesium.Math.toDegrees(cartesianPoint.longitude)
      const latitude = Cesium.Math.toDegrees(cartesianPoint.latitude)
      let height = getHeight()
      if (item.waypoints && item.waypoints[0] && !item.useGlobalHeight) {
        height = item.waypoints[0].height * 1
      }
      const position = { longitude: longitude, latitude: latitude, height }
      cartesian = Cesium.Cartesian3.fromDegrees(longitude, latitude, height)
      const isDuplicate =
        index > 0 &&
        this._isSamePoint(position, this.waypoints[this.waypoints.length - 1])

      if (!isDuplicate) {
        const endPoint = this.getPoints(cartesian, this.viewer)
        const fileRow = item
        if (fileRow?.hpr) {
          const heading = fileRow.hpr.heading - 90
          const pitch = fileRow.hpr.pitch
          FRUSTUM_CONFIG.BASE_PITCH = pitch
          FRUSTUM_CONFIG.BASE_ROLL = fileRow.hpr.roll
          FRUSTUM_CONFIG.BASE_HEADING = heading
        }
        await this.addWaypoint(position, [endPoint, cartesian], true, fileRow)

        if (this._hasStartingPoint && this.waypoints.length >= 1) {
          const oldRoute = this.routes.find((r) => r.type === 'Route' && (r.entity as any)._isDrawing)
          if (oldRoute) { this.deleteRoute(oldRoute.id) }
          this.addRoute(true)
        }
      }

      if (this._drawPointLine.length > 0) {
        const lastPoint = this._drawPointLine[this._drawPointLine.length - 1]
        this.store.commit(
          'SET_CURRPOINT_INFO',
          this.serializeRouteEntity(lastPoint)
        )
      }
    })
    setTimeout(() => {
      if (i !== null) {
        this.store.state.currentRoutePosIndex = i
        this.store.commit('SET_EDIT_CURRENT_POINT', this._drawPointLine[i])
      }
      if (cb) {
        cb()
      }
    }, 150)
  }

  /**
   * 选择航线
   * @param route 航线对象
   */
  selectRoute (route: RouteEntity): void {
    if (this._selectedRoute && this._selectedRoute.id === route.id) return

    this.clearSelection()
    this._selectedRoute = route
    this._highlightRoute(route, true)

    if (this.store) {
      this.store.commit('SET_EDIT_CURRENT_POINT', this.serializeWaypoints()[this.store.state.currentRoutePosIndex])
    }
  }

  /**
   * 通过ID选择航线
   * @param id 航线ID
   */
  selectRouteById (id: string): boolean {
    const route = this.getRouteById(id)
    if (route) {
      this.selectRoute(route)
      return true
    }
    return false
  }

  /**
   * 清除选择
   */
  clearSelection (): void {
    if (!this._selectedRoute) return

    this._highlightRoute(this._selectedRoute, false)
    this._selectedRoute = null

    if (this.store) {
      this.store.commit('SET_EDIT_CURRENT_POINT', null)
    }
  }

  /**
   * 高亮显示航线
   * @param route 航线对象
   * @param highlight 是否高亮
   * @private
   */
  private _highlightRoute (route: RouteEntity, highlight: boolean): void {
    const entity = route.entity
    const style = highlight ? this._styles.selected : this._styles

    switch (route.type) {
      case 'Waypoint':
        if (entity.point) {
          entity.point.color = style.waypoint.color
          entity.point.pixelSize = style.waypoint.pixelSize
          entity.point.outlineColor = style.waypoint.outlineColor
          entity.point.outlineWidth = style.waypoint.outlineWidth
        }
        break

      case 'Route':
        if (entity.polyline) {
          entity.polyline.width = style.route.width
          entity.polyline.material = style.route.material
        }
        break
    }
  }

  /**
   * 删除航线
   * @param id 航线ID
   */
  deleteRoute (id: string): boolean {
    const index = this.routes.findIndex((r) => r.id === id)
    if (index !== -1) {
      const route = this.routes[index]

      // 如果删除的是选中的航线，先取消选择
      if (this._selectedRoute && this._selectedRoute.id === id) {
        this.clearSelection()
      }

      // 从数据源中删除实体
      const removed = this._dataSource.entities.remove(route.entity)

      if (removed) {
        // 如果是航点，也从waypoints数组中删除
        if (route.type === 'Waypoint' && route.waypoints.length > 0) {
          const waypointIndex = this.waypoints.findIndex(
            (w) => w.id === route.waypoints[0].id
          )
          if (waypointIndex !== -1) {
            this.waypoints.splice(waypointIndex, 1)
          }
        }

        // 从航线列表中删除
        this.routes.splice(index, 1)

        if (this.store) {
          this.store.commit('SET_EDIT_CURRENT_POINT', this.waypoints[index - 1])
        }

        return true
      }
    }

    return false
  }

  /**
   * 删除选中的航线
   */
  deleteSelectedRoute (): void {
    if (!this._selectedRoute) return

    this.deleteRoute(this._selectedRoute.id)
  }

  /**
   * 通过ID获取航线
   * @param id 航线ID
   */
  getRouteById (id: string): RouteEntity | undefined {
    return this.routes.find((r) => r.id === id)
  }

  /**
   * 获取所有航线
   */
  getRoutes (): RouteEntity[] {
    return this.routes
  }

  /**
   * 获取所有航点
   */
  getWaypoints (): WaypointInfo[] {
    return this.waypoints
  }

  /**
   * 获取当前选中的航线
   */
  getSelectedRoute (): RouteEntity | null {
    return this._selectedRoute
  }

  /**
   * 清除当前绘制
   */
  clearCurrentDrawing (): void {
    this._removeDrawEvents()
  }

  /** 清除航线航点 */
  clearAllPointLine (): void {
    this.clearSelection()
    this.clearCurrentDrawing()
    this._removeAllFrustums()
    this.routes.forEach((el) => {
      if (el.type !== 'StartingPoint') {
        this._dataSource.entities.remove(el.entity)
        this._dataSource.entities.remove(el.point)
      }
    })
    this._frustumTool = null
    this._frustumMap.clear()
    this._drawPointLine = []
    this._dataSource.entities.values.forEach((entity) => {
      if (entity.id.startsWith('route_point_polylineDash_')) {
        this._dataSource.entities.remove(entity)
      }
    })
    this.routes = this.routes.filter((el) => el.type === 'StartingPoint')
    this.waypoints = []
    if (this.store) {
      this.store.commit('SET_EDIT_ROUTER_LIST', [])
      this.store.commit('SET_EDIT_CURRENT_POINT', null)
    }
  }

  /**
   * 清除所有航线航点
   */
  clearAll (): void {
    this.clearSelection()
    this.clearCurrentDrawing()
    this._removeAllFrustums() // 使用新的清除方法
    this.clearAllPolygonRoutes()
    this.clearAllStripRoutes()
    this._dataSource.entities.removeAll()
    // 清理选择处理器
    if (this._selectHandler) {
      this._selectHandler.destroy()
      this._selectHandler = null
    }
    if (this._stripEditHandler) {
      this._stripEditHandler.destroy()
      this._stripEditHandler = null
    }
    this.routes = []
    this.waypoints = []
    this._drawPointLine = []
    this._hasStartingPoint = false
    this._startingPoint = null

    if (this.store) {
      this.store.commit('SET_EDIT_ROUTER_LIST', [])
      this.store.commit('SET_EDIT_CURRENT_POINT', null)
    }
  }

  getStartingPoint (): WaypointInfo | null {
    return this._startingPoint
  }

  /**
   * 将 Cesium 实体转换为可序列化的普通对象
   */
  private serializeRouteEntity (routeEntity: RouteEntity): any {
    return {
      id: routeEntity.id,
      entityId: routeEntity.entity.id,
      type: routeEntity.type,
      // 将 Cartesian3 位置转换为可序列化的经纬度高度
      positions: routeEntity.positions,
      position: routeEntity.position,
      waypoints: routeEntity.waypoints,
      eventList: routeEntity.eventList,
      useGlobalTurnParam: routeEntity.useGlobalTurnParam,
      waypointTurnMode: routeEntity.waypointTurnMode,
      waypointTurnDampingDist: routeEntity.waypointTurnDampingDist,
      useGlobalHeadingParam: routeEntity.useGlobalHeadingParam,
      waypointHeadingParam: routeEntity.waypointHeadingParam,
      useGlobalSpeedParam: routeEntity.useGlobalSpeedParam,
      waypointSpeed: routeEntity.waypointSpeed,
      hpr: routeEntity.hpr
      // 不包含 Cesium Entity 对象
    }
  }

  /**
   * 将航点列表转换为可序列化的格式
   */
  private serializeWaypoints (): any[] {
    return this.waypoints.map((wp) => ({
      ...wp,
      // 确保没有包含任何 Cesium 对象
    }))
  }

  /**
   * 获取可序列化的绘制点线数据
   */
  private getSerializableDrawPointLine (): any[] {
    return this._drawPointLine.map((route) => this.serializeRouteEntity(route))
  }

  /**
   * 添加视锥体
   * @param position 位置信息
   */
  addFrustum (position: any, entity: Cesium.Entity): void {
    if (!this._frustumTool) {
      // 确保 frustumTool 已初始化
      this._frustumTool = new FrustumTool({
        position: Cesium.Cartesian3.fromDegrees(position.longitude, position.latitude, position.height),
        heading: FRUSTUM_CONFIG.BASE_HEADING,
        pitch: FRUSTUM_CONFIG.BASE_PITCH,
        roll: FRUSTUM_CONFIG.BASE_ROLL,
        fov: FRUSTUM_CONFIG.BASE_FOV / FRUSTUM_CONFIG.BASE_NEAR,
        near: FRUSTUM_CONFIG.BASE_NEAR,
        far: FRUSTUM_CONFIG.BASE_FAR,
        aspectRatio: 0.75,
        color: [76, 201, 240], // RGB数组格式
        viewer: this.viewer,
        glbModelShow: true,
        frustumLineShow: true,
        outlineShow: true,
        frustumShow: true,
      })
      this._frustumMap.set(entity.id, this._frustumTool)
    } else {
      const cartesian = Cesium.Cartesian3.fromDegrees(position.longitude, position.latitude, position.height)
      this._frustumTool.update(cartesian, FRUSTUM_CONFIG.BASE_HEADING - 90, FRUSTUM_CONFIG.BASE_PITCH)
      // this._frustumTool.updateFrustum(cartesian, FRUSTUM_CONFIG.BASE_HEADING - 90, FRUSTUM_CONFIG.BASE_PITCH)
    }
  }

  getEntity (id: string) {
    return this._dataSource.entities.getById(id)
  }

  /**
   * 根据航点ID获取视锥体
   * @param waypointId 航点ID
   */
  private _getFrustumByWaypointId (waypointId: string): FrustumTool | null {
    return (this._frustumMap.get(waypointId) as FrustumTool) || null
  }

  /**
   * 显示指定航点的视锥体
   * @param waypointId 航点ID
   */
  private _showFrustum (waypointId: string): void {
    const frustum = this._getFrustumByWaypointId(waypointId)
    if (frustum) {
      frustum.show()
    }
  }

  /** 设置当前视锥体 */
  setFrustum (waypointId: string): void {
    const position = this._drawPointLine[this.store.state.currentRoutePosIndex]
    this._frustumTool?.update(position.position, position.hpr.heading - 90, position.hpr.pitch)
    this._frustumTool?.show()
  }

  /** 隐藏当前视椎体 */
  hideCurrentFrustum (): void {
    this._frustumTool?.hide()
  }

  /**
   * 显示给定航点的视锥体。
   *
   * @param waypointId 航点的唯一标识符。
   */
  showFrustum (waypointId: string): void {
    this._showFrustum(waypointId)
  }

  /**
   * 隐藏指定航点的视锥体
   * @param waypointId 航点ID
   */
  private _hideFrustum (waypointId: string): void {
    const frustum = this._getFrustumByWaypointId(waypointId)
    if (frustum) {
      frustum.hide()
    }
  }

  /**
   * 隐藏指定路径点的视锥体
   *
   * @param waypointId 路径点的ID
   */
  hideFrustum (waypointId: string): void {
    this._hideFrustum(waypointId)
  }

  /**
   * 移除所有视锥体
   */
  private _removeAllFrustums (): void {
    this._frustumMap.forEach((frustum, waypointId) => {
      frustum.clearAll()
    })
    this._frustumTool?.clearAll()
    this._frustumMap.clear()
    this._frustumTool = null
  }

  getAllFrustums (): Map<string, FrustumTool> {
    return this._frustumMap
  }

  /**
   * 移除绘制鼠标事件
   * @private
   */
  private _removeDrawEvents (): void {
    this._removePolygonEvents()
    this._removeStripCenterLineEvents()
    this._clearStripTempCenterLine()
    if (this._drawHandler) {
      this._drawHandler.removeInputAction(
        Cesium.ScreenSpaceEventType.LEFT_CLICK
      )
      this._drawHandler.destroy()
      this._drawHandler = null
    }
    if (this._stripEditHandler) {
      this._stripEditHandler.destroy()
      this._stripEditHandler = null
    }
  }

  /**
   * 添加实体
   * @param options 实体选项
   * @param type 实体类型
   * @private
   */
  private _addEntity (options: any, type?: string): Cesium.Entity {
    const entity = this._dataSource.entities.add(options)
    return entity
  }

  private getPoints (cartesian, viewer) {
    const point = Cesium.Cartographic.fromCartesian(cartesian)
    const longitude = Cesium.Math.toDegrees(point.longitude)
    const latitude = Cesium.Math.toDegrees(point.latitude)
    const height = viewer.scene.globe.getHeight(
      Cesium.Cartographic.fromDegrees(longitude, latitude, 0)
    )
    return Cesium.Cartesian3.fromDegrees(longitude, latitude, height)
  }

  /**
   * 转化为真实点位经纬度
   * @param cartesian 笛卡尔坐标
   */
  conversion (cartesian: Cesium.Cartesian3): any {
    const cartographic = Cesium.Cartographic.fromCartesian(cartesian)
    const pointInfo: any = {}
    pointInfo.longitude = Cesium.Math.toDegrees(cartographic.longitude) // 经度
    pointInfo.latitude = Cesium.Math.toDegrees(cartographic.latitude) // 纬度
    // 获取地形高度，如果地形不可用则使用椭球高度
    let height = cartographic.height
    try {
      const terrainHeight = this.viewer.scene.globe.getHeight(cartographic)
      if (terrainHeight !== undefined && !isNaN(terrainHeight)) {
        height = terrainHeight
      }
    } catch (e) {
      console.warn('获取地形高度失败，使用椭球高度:', e)
    }

    pointInfo.height = Math.max(0, Math.floor(height)) // 高度，取非负值并向下取整
    return pointInfo
  }

  /**
   * 点位样式选项（支持放大显示）
   * @param index 索引
   * @param isDrawing 是否在绘制中
   */
  pointOption (index: number, isDrawing: boolean = false): any {
    const baseSize = 26
    const size = isDrawing ? baseSize * 1.5 : baseSize

    return {
      point: {
        pixelSize: 10,
        color: new Cesium.Color(1, 1, 1, 1),
        disableDepthTestDistance: 3e6,
        show: true,
      },
      billboard: {
        image:
          'data:image/svg+xml;base64,' +
          // btoa(`<svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 50 50">
          //     <circle cx="25" cy="25" r="20" fill="#1E90FF" stroke="#00008B" stroke-width="1"/>
          //     <text x="25" y="25" font-size="16" text-anchor="middle" fill="white" dy=".3em">${index}</text>
          // </svg>`),
          btoa(`<svg width="200" height="200" xmlns="http://www.w3.org/2000/svg">
  <polygon points="0,0 100,200 200,0" fill="#00C853"/>
  <text x="100" y="100" text-anchor="middle" font-family="Arial" font-size="60" font-weight="bold" fill="white">${index}</text>
</svg>`),
        width: size,
        height: size,
        // 使用绝对高度参考，避免地形影响
        heightReference: Cesium.HeightReference.NONE,
        pixelOffset: new Cesium.Cartesian2(0, -15),
        horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
        verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
        disableDepthTestDistance: Number.POSITIVE_INFINITY, // 始终显示
        distanceDisplayCondition: new Cesium.DistanceDisplayCondition(
          0,
          500000
        ),
      },
    }
  }

  /**
   * 检查两个点是否相同
   * @param point1 点1
   * @param point2 点2
   * @param tolerance 容差
   * @private
   */
  private _isSamePoint (
    point1: any,
    point2: any,
    tolerance: number = 0.0001
  ): boolean {
    if (!point1 || !point2) return false

    const latDiff = Math.abs(point1.latitude - point2.latitude)
    const lonDiff = Math.abs(point1.longitude - point2.longitude)
    const heightDiff = Math.abs(point1.height - point2.height)

    return latDiff < tolerance && lonDiff < tolerance && heightDiff < tolerance
  }

  /**
   * 销毁工具，释放资源
   */
  destroy (): void {
    this._removeDrawEvents()
    this.clearAll()
    this.stopStripDrawing()
    this.viewer.dataSources.remove(this._dataSource)
  }
}
